/*
 * ------------ Theme Common JS ------------
 */

if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}
// Swiper's a11y module overwrites the nav buttons' aria-label with its own
// hardcoded English message, so seed every instance with the localized set.
if (window.Swiper?.extendDefaults && window.theme?.a11y) {
  window.Swiper.extendDefaults({
    a11y: {
      nextSlideMessage: window.theme.a11y.nextSlide,
      prevSlideMessage: window.theme.a11y.previousSlide,
      firstSlideMessage: window.theme.a11y.firstSlide,
      lastSlideMessage: window.theme.a11y.lastSlide,
      paginationBulletMessage: window.theme.a11y.goToSlide,
    },
  });
}

// Swiper disables the arrow that reaches the edge, and a disabled button drops the
// focus it holds to <body>, so the keyboard user is handed the opposite arrow.
document.addEventListener("click", (event) => {
  const button = event.target.closest?.(".slider-nav-button");
  if (!button) return;
  requestAnimationFrame(() => {
    if (!button.disabled) return;
    const active = document.activeElement;
    if (active && active !== document.body && active !== button) return;
    const sibling = Array.from(
      button.parentElement?.querySelectorAll(".slider-nav-button") || []
    ).find((el) => el !== button && !el.disabled);
    sibling?.focus({ preventScroll: true });
  });
});

const PUB_SUB_EVENTS = {
  cartUpdate: "cart-update",
  quantityUpdate: "quantity-update",
  variantChange: "variant-change",
  cartError: "cart-error",
};

let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}

// Shared initialization script initSectionSliderLayout is used for sliders across sections (Popular products, Featured blog, Collection list, Multicolumn, etc.). Sliders can be initialized per section if needed.
function initSectionSliderLayout(section) {
  const clipEl = section.closest("[data-section-has-card-slider='true']");
  if (!clipEl) return;

  const sliderEl = section.querySelector(".js-section-card-slider");
  if (!sliderEl) return;

  // Tab-focusing a card in an off-screen slide makes the browser scroll this
  // overflow:hidden box to reveal it, which drags the whole section — slider
  // controls included — sideways. Swiper's a11y slides the card into view on
  // its own, so keep the clip box pinned. The listener dies with the section.
  if (!clipEl.dataset.sliderScrollPinned) {
    clipEl.dataset.sliderScrollPinned = "true";
    clipEl.addEventListener("scroll", () => {
      if (clipEl.scrollLeft !== 0) clipEl.scrollLeft = 0;
      if (clipEl.scrollTop !== 0) clipEl.scrollTop = 0;
    });
  }

  const paginationEl = section.querySelector(".slider-pagination");
  const nextBtn = section.querySelector(".slider-nav-button--next");
  const prevBtn = section.querySelector(".slider-nav-button--prev");
  const hasNavigation = sliderEl.getAttribute("data-navigation") === "true";
  const hasPagination = sliderEl.getAttribute("data-pagination") === "true";

  const sliderLoop = sliderEl.getAttribute("data-loop") === "true";
  const hasSliderAutoplay = sliderEl.getAttribute("data-autoplay") === "true";
  const pauseAutoplayOnHover =
    sliderEl.getAttribute("data-autoplay-pause-on-hover") === "true";
  const autoplayDelay = Number(sliderEl.dataset.autoplayDelay || 8000);
  const slidesPerRow = Number(sliderEl.dataset.slidesPerRow || 4);
  const slidesPerRowMobile = Number(sliderEl.dataset.slidesPerRowMobile || 1);
  // opt-in single-step breakpoint (eg. product hotspots: 2 cards until 1160px, then 3)
  // instead of the default 576/990/1200 progression
  const slidesPerRowBreakpoint = Number(
    sliderEl.dataset.slidesPerRowBreakpoint || 0
  );
  // opt-in: pushes the last step of the default progression past $xl for cards
  // that need more room than 1200px gives them (eg. testimonials: 4-up at 1400)
  const slidesPerRowDesktopBreakpoint = Number(
    sliderEl.dataset.slidesPerRowDesktopBreakpoint || 1200
  );

  // Centering the active slide in loop mode needs more slides than filling the box
  // does: Swiper rounds an even slidesPerView up to odd and parks half a screen of
  // buffer on each side. Below that threshold the track visibly runs out and leaves
  // an empty strip, so center only when the slide count can carry it — measured
  // against the desktop slidesPerView, the strictest breakpoint. A section can also
  // opt out entirely when its slides should stay flush with the container edges.
  const slidesTotal = sliderEl.querySelectorAll(".swiper-slide").length;
  const centeredView = slidesPerRow % 2 === 0 ? slidesPerRow + 1 : slidesPerRow;
  const centeredMinSlides = centeredView + Math.ceil(centeredView / 2);
  const allowCenteredLoop = sliderEl.dataset.loopCentered !== "false";
  const centeredLoop =
    sliderLoop && allowCenteredLoop && slidesTotal >= centeredMinSlides;
  // lets slider.scss keep the edge bleed only where centering fills both sides
  sliderEl.classList.toggle("section-card-slider--centered", centeredLoop);

  const slidesPerRow576 = slidesPerRow > 1 ? 2 : 1;
  const slidesPerRow990 =
    slidesPerRow > 2 ? slidesPerRow576 + 1 : slidesPerRow576;
  const slidesPerRowDesktop = slidesPerRow >= 4 ? 4 : slidesPerRow;

  const breakpoints = slidesPerRowBreakpoint
    ? {
        // matches the shared .section-card-wrapper--N max-width gap (1.6rem)
        // that kicks in from $sm — keeps Swiper's own slide width in sync
        // with the CSS max-width so overflow:hidden doesn't clip a few px
        // off the active slide's edge
        576: {
          slidesPerView: slidesPerRow576,
          spaceBetween: 16,
        },
        [slidesPerRowBreakpoint]: {
          slidesPerView: slidesPerRow,
          spaceBetween: 16,
        },
      }
    : {
        576: {
          slidesPerView: slidesPerRow576,
          spaceBetween: 16,
        },
        990: {
          slidesPerView: slidesPerRow990,
          spaceBetween: 16,
        },
        [slidesPerRowDesktopBreakpoint]: {
          slidesPerView: slidesPerRowDesktop,
          spaceBetween: 16,
        },
      };

  // With loop on, Swiper can't slide a focused slide into view (see scrollOnFocus
  // below), so Tab would otherwise stop on cards parked off-screen. Take the
  // off-screen ones out of the tab order and let the arrows bring them in.
  const syncLoopSlideFocusability = (swiper) => {
    swiper.slides.forEach((slide) => {
      const keepFocusable =
        !sliderLoop ||
        slide.classList.contains("swiper-slide-visible") ||
        slide.contains(document.activeElement);
      slide.toggleAttribute("inert", !keepFocusable);
    });
  };

  const sliderSettings = {
    loop: sliderLoop,
    // Loop mode centers the active slide so the track always has slides on both
    // sides of the box: the first slide opens centered and the neighbours run off
    // the leading and trailing edge into the container padding.
    centeredSlides: centeredLoop,
    slidesPerView: slidesPerRowMobile,
    spaceBetween: 12,
    speed: 800,
    // spelled `watchSlidesProgress`: it feeds Swiper's `visibleSlides`, which the
    // a11y module checks before sliding on focus — without it every Tab into an
    // already-visible card jumps the slider
    watchSlidesProgress: true,
    allowTouchMove: true,
    mousewheel: {
      forceToAxis: true,
    },
    a11y: {
      // Swiper's a11y module calls slideToLoop() on focus when loop is
      // enabled, which reorders slide DOM nodes to keep the infinite-loop
      // illusion. Doing that mid-Tab-sequence corrupts the browser's tab
      // order and traps focus inside the slider, so only let Swiper steer
      // focus-driven sliding when loop is off.
      scrollOnFocus: !sliderLoop,
    },
    autoplay: hasSliderAutoplay
      ? {
          delay: autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: pauseAutoplayOnHover,
        }
      : false,
    pagination: hasPagination
      ? paginationEl?.classList.contains("slider-pagination--progress-line")
        ? {
            el: paginationEl,
            type: "progressbar",
          }
        : {
            el: paginationEl,
            clickable: true,
          }
      : false,
    navigation: hasNavigation
      ? {
          nextEl: nextBtn,
          prevEl: prevBtn,
          disabledClass: "swiper-button-disabled",
        }
      : false,
    breakpoints,
    on: {
      init: syncLoopSlideFocusability,
      resize: syncLoopSlideFocusability,
      // a filter tab swap re-lays the track through update() without a transition
      update: syncLoopSlideFocusability,
      autoplayTimeLeft(s, time, progress) {
        if (hasPagination && paginationEl) {
          paginationEl.style.setProperty("--slider-progress", 1 - progress);
        }
      },
      // Safari fires spurious mouseenter/mouseleave (:hover) on card content
      // that slides underneath a stationary cursor during a trackpad/touch
      // swipe, which flickers any hover-revealed card content (eg. Overlay
      // layout). Disabling pointer-events for the swipe's duration stops the
      // slides from receiving hover state while they're moving.
      sliderFirstMove(swiper) {
        swiper.el.classList.add("is-swiping");
      },
      touchEnd(swiper) {
        if (!swiper.animating) {
          swiper.el.classList.remove("is-swiping");
        }
      },
      transitionEnd(swiper) {
        swiper.el.classList.remove("is-swiping");
        syncLoopSlideFocusability(swiper);
      },
    },
  };

  new Swiper(sliderEl, sliderSettings);

  if (hasSliderAutoplay && sliderEl.swiper) {
    // Keyboard focus holds the autoplay too: sliding on would carry the focused
    // card out of view.
    let onScreen = false;
    let focusHeld = false;
    const syncAutoplay = () => {
      if (onScreen && !focusHeld) {
        sliderEl.swiper?.autoplay.resume();
      } else {
        sliderEl.swiper?.autoplay.pause();
      }
    };

    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        onScreen = entry.isIntersecting;
        syncAutoplay();
      });
    });

    sectionObserver.observe(section);

    sliderEl._autoplayFocusAbort?.abort();
    const focusController = new AbortController();
    sliderEl._autoplayFocusAbort = focusController;
    sliderEl.addEventListener(
      "focusin",
      () => {
        focusHeld = true;
        syncAutoplay();
      },
      { signal: focusController.signal }
    );
    sliderEl.addEventListener(
      "focusout",
      (event) => {
        if (sliderEl.contains(event.relatedTarget)) return;
        focusHeld = false;
        syncAutoplay();
      },
      { signal: focusController.signal }
    );
  }
}

const ARTICLE_CARD_OVERLAY_REVEAL_MIN_WIDTH = 750; // $md in src/scss/base/variables/grid.scss

function updateOverlayArticleCardRevealShift(scope = document) {
  if (!scope || typeof scope.querySelectorAll !== "function") return;

  const cards = [];

  if (
    typeof scope.matches === "function" &&
    scope.matches(".article-card--overlay")
  ) {
    cards.push(scope);
  }

  cards.push(...scope.querySelectorAll(".article-card--overlay"));

  const isRevealEligible = window.matchMedia(
    `(min-width: ${ARTICLE_CARD_OVERLAY_REVEAL_MIN_WIDTH}px)`
  ).matches;

  cards.forEach((card) => {
    card.classList.remove("article-card--overlay-ready");
    card.classList.remove("article-card--overlay-animate");

    if (!isRevealEligible) {
      card.style.removeProperty("--article-card-overlay-reveal-shift");
      return;
    }

    const excerpt = card.querySelector(".article-card__excerpt");
    const meta = card.querySelector(".article-card__meta");
    const revealElements = [excerpt, meta].filter(Boolean);

    if (revealElements.length === 0) {
      card.style.removeProperty("--article-card-overlay-reveal-shift");
      card.classList.remove("article-card--overlay-ready");
      card.classList.remove("article-card--overlay-animate");
      return;
    }

    let revealHeight = 0;

    revealElements.forEach((element) => {
      const styles = window.getComputedStyle(element);
      const marginTop = parseFloat(styles.marginTop) || 0;
      const marginBottom = parseFloat(styles.marginBottom) || 0;

      revealHeight += element.offsetHeight + marginTop + marginBottom;
    });

    card.style.setProperty(
      "--article-card-overlay-reveal-shift",
      `${Math.ceil(revealHeight)}px`
    );

    card.classList.add("article-card--overlay-ready");

    const enableOverlayAnimation = () => {
      card.classList.add("article-card--overlay-animate");
    };

    if (card.matches(":hover")) {
      const onMouseLeave = () => {
        card.removeEventListener("mouseleave", onMouseLeave);
        enableOverlayAnimation();
      };

      card.addEventListener("mouseleave", onMouseLeave, { once: true });
      return;
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(enableOverlayAnimation);
    });
  });
}

// Message-style textareas (.text-area) follow their value instead of scrolling: the
// height is measured from scrollHeight, clamped by the min-/max-height in form.scss,
// so only a value past the cap scrolls. Resetting to "auto" first is what lets the
// field shrink again when text is deleted.
function autoGrowTextArea(field) {
  if (!(field instanceof HTMLTextAreaElement)) return;

  const styles = window.getComputedStyle(field);
  const borderHeight =
    (parseFloat(styles.borderTopWidth) || 0) +
    (parseFloat(styles.borderBottomWidth) || 0);

  field.style.height = "auto";
  field.style.height = `${field.scrollHeight + borderHeight}px`;
}

function initAutoGrowTextAreas(scope = document) {
  if (typeof scope?.querySelectorAll !== "function") return;

  scope.querySelectorAll("textarea.text-area").forEach(autoGrowTextArea);
}

// Delegated, so a field that arrives later (section re-render, cart refresh, quick view)
// grows without being re-bound
document.addEventListener("input", (event) => {
  const field = event.target;
  if (field?.classList?.contains("text-area")) autoGrowTextArea(field);
});

document.addEventListener("DOMContentLoaded", () => {
  const sectionsWithCardSlider = document.querySelectorAll(
    "[data-section-has-card-slider='true']"
  );
  sectionsWithCardSlider.forEach(initSectionSliderLayout);
  updateOverlayArticleCardRevealShift(document);
  initAutoGrowTextAreas(document);
});

document.addEventListener("shopify:section:load", function (event) {
  if (typeof event.target?.querySelector !== "function") return;

  const section = event.target?.querySelector(
    "[data-section-has-card-slider='true']"
  );
  if (section) initSectionSliderLayout(section);

  updateOverlayArticleCardRevealShift(event.target);
  initAutoGrowTextAreas(event.target);
});

function lockScrollBodyOnIos(openScrollY) {
  if (navigator.userAgent.indexOf("iPhone") > -1) {
    document.documentElement.style.setProperty(
      "--current-body-scroll",
      `${-1 * openScrollY}px`
    );
    document.documentElement.style.setProperty("scroll-behavior", "unset");
    document.body.classList.add("forced-scroll-locked");
  }
}

function unlockScrollBodyOnIos(openScrollY) {
  if (navigator.userAgent.indexOf("iPhone") > -1) {
    document.body.classList.remove("forced-scroll-locked");
    document.documentElement.scrollTop = openScrollY;
    document.documentElement.style.removeProperty("--current-body-scroll");
    document.documentElement.style.removeProperty("scroll-behavior");
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  ).filter((element) => element.offsetParent !== null);
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute("aria-expanded", "false");

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (summary.closest("header-mobile-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  removeTrapFocus();
  // read by dialogs that open over another one and must hand the trap back
  trapFocusHandlers.container = container;

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return;

    // Recompute on every Tab — modals load content (installments, dynamic
    // checkout) after the trap is set, so a cached list goes stale.
    const elements = getFocusableElements(container);
    if (elements.length === 0) return;

    const first = elements[0];
    const last = elements[elements.length - 1];
    const target = event.target;

    if (!container.contains(target)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    // Shadow-DOM hosts (payment terms, dynamic checkout) retarget events to the
    // host, which is absent from the list — hijack Tab only at real boundaries
    // and let the browser walk everything in between; focusin covers escapes.
    if (event.shiftKey) {
      if (target === first || target === container) {
        event.preventDefault();
        last.focus();
      }
    } else if (target === last || target === container) {
      event.preventDefault();
      first.focus();
    }
  };

  trapFocusHandlers.focusin = (event) => {
    if (container.contains(event.target)) return;

    const elements = getFocusableElements(container);
    elements[0]?.focus();
  };

  document.addEventListener("keydown", trapFocusHandlers.keydown, true);
  document.addEventListener("focusin", trapFocusHandlers.focusin, true);

  elementToFocus?.focus();
}

function pauseAllMedia(parentElement = document, exceptElement = null) {
  parentElement.querySelectorAll(".js-youtube").forEach((video) => {
    if (exceptElement && exceptElement.contains(video)) return;
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  parentElement.querySelectorAll(".js-vimeo").forEach((video) => {
    if (exceptElement && exceptElement.contains(video)) return;
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  parentElement.querySelectorAll("video").forEach((video) => {
    if (exceptElement && exceptElement.contains(video)) return;
    video.pause();
  });
  parentElement.querySelectorAll("product-model").forEach((model) => {
    if (exceptElement && exceptElement.contains(model)) return;
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin, true);
  document.removeEventListener("keydown", trapFocusHandlers.keydown, true);
  trapFocusHandlers.container = null;

  if (elementToFocus) elementToFocus.focus();
}

function formatMoney(cents, format = "") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  cents = parseInt(cents, 10);

  let value = "";
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || theme.moneyFormat;

  function formatWithDelimiters(
    number,
    precision = 2,
    thousands = ",",
    decimal = "."
  ) {
    if (isNaN(number) || number == null) {
      return "0";
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split(".");
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`
    );
    const centsAmount = parts[1] ? decimal + parts[1] : "";

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2, ",", ".");
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0, ",", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ", ".");
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ",");
      break;
    case "amount_with_period_and_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    default:
      value = formatWithDelimiters(cents, 2, ",", ".");
  }

  return formatString.replace(placeholderRegex, value);
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

window.addEventListener(
  "resize",
  debounce(() => {
    updateOverlayArticleCardRevealShift(document);
  }, 150)
);

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

// The Cart AJAX API answers legitimate cart errors with a JSON body (422 for stock, and
// friends) that callers inspect, so the status alone can't decide — and it labels that
// body application/json or text/javascript depending on the Accept it was called with,
// so neither can the content type. Only an unparsable payload is a transport failure: a
// throttle page, a maintenance page, the storefront password page. Left to response.json()
// that surfaces as a bare "Unexpected token '<'" SyntaxError with nothing pointing at the
// request, so it is rejected with the response details instead.
function parseJsonResponse(response) {
  return response.text().then((text) => {
    try {
      return JSON.parse(text);
    } catch {
      throw new Error(
        `Expected JSON from ${response.url} — got ${response.status} ${
          response.statusText
        } (${response.headers.get("Content-Type") || "no content type"})`
      );
    }
  });
}

(function () {
  function copyURI(e, copyLink) {
    e.preventDefault();

    const url = window.location.href;

    const handleCopy = (btn, success) => {
      if (success) {
        btn.classList.add("copied");
        setTimeout(() => btn.classList.remove("copied"), 1000);
      } else {
        btn.classList.add("notCopied");
        setTimeout(() => btn.classList.remove("notCopied"), 1000);
      }
    };

    // Modern Clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => handleCopy(copyLink, true))
        .catch(() => handleCopy(copyLink, false));
    } else {
      // Fallback for unsupported browsers
      const textArea = document.createElement("textarea");
      textArea.value = url;
      textArea.style.position = "fixed"; // avoid scrolling
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand("copy");
        handleCopy(copyLink, successful);
      } catch (err) {
        handleCopy(copyLink, false);
      }

      document.body.removeChild(textArea);
      // the helper field took the focus; hand it back to the link
      copyLink.focus();
    }
  }

  const copyLinks = document.querySelectorAll(".copy-btn");
  copyLinks.forEach((copyLink) =>
    copyLink.addEventListener("click", (e) => copyURI(e, copyLink))
  );
})();

if (!Element.prototype.replaceChildren) {
  Element.prototype.replaceChildren = function (...nodes) {
    while (this.firstChild) this.removeChild(this.firstChild);
    this.append(...nodes);
  };
}

// [Theme custom event] - Cart UI refresh event
document.addEventListener("cart:refresh", (event) => {
  const sectionsToUpdate = [
    {
      id: "cart-icon-bubble",
      selector: ".cart-count-bubble",
    },
    // Both live cart surfaces come from the same `cart-drawer` section.
    {
      id: "cart-drawer",
      selector: "#CartDrawerDesktop",
    },
    {
      id: "cart-drawer",
      selector: "#CartDrawerMobile",
    },
    {
      id: "main-cart",
      selector: ".main-cart",
    },
  ];

  const sectionIds = [
    ...new Set(sectionsToUpdate.map((section) => section.id)),
  ].join(",");

  fetch(`${routes.cart_url}?sections=${sectionIds}`)
    .then(parseJsonResponse)
    .then((sections) => {
      sectionsToUpdate.forEach((section) => {
        const sectionHTML = sections[section.id];
        if (!sectionHTML) return;

        const html = new DOMParser().parseFromString(sectionHTML, "text/html");
        const selector = section.selector;

        const sourceElement = html.querySelector(selector);
        const destinationElements = document.querySelectorAll(selector);

        if (sourceElement && destinationElements.length > 0) {
          destinationElements.forEach((destinationElement) => {
            destinationElement.innerHTML = sourceElement.innerHTML;
            initAutoGrowTextAreas(destinationElement);
            if (sourceElement.classList.contains("is-empty")) {
              destinationElement.classList.add("is-empty");
            } else {
              destinationElement.classList.remove("is-empty");
            }
          });
        }
      });
    })
    .catch((error) => {
      console.error("Error updating cart:", error);
    });
});

/*
 * ------------ Theme Web Components ------------
 */

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.changeEvent = new Event("change", { bubbles: true });
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.input = this.querySelector("input");
    // Cart lines accept 0 (it removes the line); product forms do not
    this.isCartItem = Boolean(this.closest(".cart-item, .cart-item-row"));

    this.querySelectorAll("button").forEach((button) => {
      button.addEventListener("click", this.onButtonClick.bind(this));
    });

    this.setMinimumDisable();

    var eventList = ["paste", "input"];
    for (const event of eventList) {
      this.input.addEventListener(event, (e) => {
        const value = e.currentTarget.value;
        const numberRegex = this.isCartItem ? /^\d*$/ : /^0*?[1-9]\d*$/;

        if (numberRegex.test(value) || value === "") {
        } else {
          e.currentTarget.value = 1;
        }

        this.setMinimumDisable();
      });
    }

    this.input.addEventListener("focusout", (e) => {
      if (e.currentTarget.value === "") {
        e.currentTarget.value = 1;
        this.setMinimumDisable();
      }
    });
  }

  setMinimumDisable() {
    if (!this.input) return;
    const value = Number(this.input.value);

    const minusBtn = this.querySelector('button[name="minus"]');
    if (minusBtn) {
      const minValue = this.input.min === "" ? 1 : Number(this.input.min);
      minusBtn.classList.toggle("disabled", value <= minValue);
    }

    const plusBtn = this.querySelector('button[name="plus"]');
    if (plusBtn) {
      const hasMax = this.input.max !== "";
      plusBtn.classList.toggle(
        "disabled",
        hasMax && value >= Number(this.input.max)
      );
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === "plus" ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }

    this.setMinimumDisable();
  }
}

if (!customElements.get("quantity-input")) {
  customElements.define("quantity-input", QuantityInput);
}

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetBtn = this.querySelector(".js-search-input-reset");

    this.input?.form?.addEventListener("reset", this.onFormReset.bind(this));
    this.input?.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    this.resetBtn?.addEventListener("click", this.onFormReset.bind(this));

    // The field is a padded box around the input: a click on the icon or on the
    // padding has to land in the input, not fall through to nothing. mousedown
    // (not click) so the caret is never placed and then taken away.
    this.querySelector(".search-input__field")?.addEventListener(
      "mousedown",
      (event) => {
        if (!this.input || event.target === this.input) return;
        if (event.target.closest(".js-search-input-reset")) return;
        event.preventDefault();
        this.input.focus();
      }
    );
  }

  connectedCallback() {
    this.toggleResetBtn(this.input?.value);
  }

  toggleResetBtn(value) {
    if (!this.resetBtn) return;

    if (value) {
      this.resetBtn.style.display = "flex";
    } else {
      this.resetBtn.style.display = "none";
    }
  }

  onChange(event) {
    this.toggleResetBtn(event.target.value);
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();

    if (this.input) {
      if (this.closest(".search-section") && window.location.search) {
        const params = new URLSearchParams(window.location.search);
        const q = params.get("q");
        if (this.input.value !== q) {
          setTimeout(() => {
            this.input.value = q;
            this.input.focus();
            this.toggleResetBtn(this.input.value);
          }, 0);
        } else {
          const cleanURL = window.location.pathname;
          window.location.href = cleanURL;
        }
      } else {
        this.input.value = "";
        this.input.focus();
        this.toggleResetBtn("");
      }
    }
  }
}

if (!customElements.get("search-form")) {
  customElements.define("search-form", SearchForm);
}

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");
    this.closeBtn = this.querySelector(".modal-close-button");
    const summaryElements = this.querySelectorAll("summary");
    this.addAccessibilityAttributes(summaryElements);

    this.headerWrapper = document.querySelector(".header-wrapper");
    if (this.headerWrapper) this.headerWrapper.preventHide = false;

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );

    this.closeBtn?.addEventListener(
      "click",
      this.onCloseButtonClick.bind(this)
    );
  }

  addAccessibilityAttributes(summaryElements) {
    summaryElements.forEach((element) => {
      element.setAttribute("role", "button");
      element.setAttribute("aria-expanded", false);
      element.setAttribute("aria-controls", element.nextElementSibling.id);
    });
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(this.mainDetailsToggle.querySelector("summary"))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute("open");

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(summaryElement)
        : this.openMenuDrawer(summaryElement);
    } else {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );

      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
      });
    }
  }

  openMenuDrawer(summaryElement) {
    if (this.headerWrapper) this.headerWrapper.preventHide = true;
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-drawer`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event !== undefined) {
      this.mainDetailsToggle.classList.remove("menu-opening");
      this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
        details.removeAttribute("open");
        details.classList.remove("menu-opening");
      });
      this.mainDetailsToggle
        .querySelector("summary")
        .setAttribute("aria-expanded", false);
      document.body.classList.remove(`overflow-hidden-drawer`);
      removeTrapFocus(elementToFocus);
      this.closeAnimation(this.mainDetailsToggle);
      if (this.headerWrapper) this.headerWrapper.preventHide = false;
    }
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    if (!detailsElement) return;
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    detailsElement?.classList.remove("menu-opening");
    removeTrapFocus();
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

if (!customElements.get("menu-drawer")) {
  customElements.define("menu-drawer", MenuDrawer);
}

class HeaderMobileDrawer extends MenuDrawer {
  constructor() {
    super();
    this.header = document.querySelector(".shopify-section-header");
    this.headerWrapper = document.querySelector(".header-wrapper");
    if (this.headerWrapper) this.headerWrapper.preventHide = false;
    this.openBtn = this.querySelector(".js-menu-drawer-open");
    this.closeBtn = this.querySelector(".js-menu-drawer-close");
    this.isOpen = false;

    this.closeBtn?.addEventListener("click", () => {
      this.closeMenuDrawer(this.openBtn);
    });

    // A dialog opened from the drawer (region modal) closes on Escape keydown
    // and hands focus back here, so the keyup of that same press lands in the
    // drawer. Only an Escape pressed inside the drawer may close a layer.
    this.escapePressedInside = false;
    this.addEventListener("keydown", (event) => {
      if (event.key === "Escape") this.escapePressedInside = true;
    });
  }

  openMenuDrawer(summaryElement) {
    if (this.headerWrapper) this.headerWrapper.preventHide = true;
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add("overflow-hidden-mobile-menu");
    this.isOpen = true;
    this.openScrollY = window.scrollY;

    // styles for forced disable body scroll on iOS
    if (typeof window.lockScrollBodyOnIos === "function") {
      this.openScrollY = window.scrollY;
      window.lockScrollBodyOnIos(this.openScrollY);
    }
  }

  closeMenuDrawer(elementToFocus) {
    if (!elementToFocus) return;
    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
      details.querySelector("summary")?.setAttribute("aria-expanded", false);
    });
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    document.body.classList.remove("overflow-hidden-mobile-menu");
    document.body.classList.remove("overflow-hidden");
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    // close every mobile mega panel (submenu / menu / cards) left open
    this.querySelectorAll(".mega-submenu-mobile--visible").forEach((panel) => {
      panel.classList.remove("mega-submenu-mobile--visible");
      panel
        .closest(".header-mobile-menu__item")
        ?.querySelector(".js-mega-trigger")
        ?.setAttribute("aria-expanded", "false");
    });

    // close search modal if open
    const searchModal = document.querySelector(".header-search-modal");
    if (searchModal && searchModal.getAttribute("open")) {
      const headerSearch = document.querySelector("header-search");
      if (headerSearch && typeof headerSearch.close === "function") {
        headerSearch.close(null, elementToFocus);
      }
    }

    // styles for forced disable body scroll on iOS
    if (typeof window.unlockScrollBodyOnIos === "function") {
      window.unlockScrollBodyOnIos(this.openScrollY);
    }

    if (this.headerWrapper) this.headerWrapper.preventHide = false;
    this.isOpen = false;
  }

  // Escape unwinds one layer at a time: a nested accordion, then a mega panel,
  // then the drawer itself.
  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;
    if (!this.escapePressedInside) return;
    this.escapePressedInside = false;

    const openDetailsElement = event.target.closest("details[open]");
    if (openDetailsElement && openDetailsElement !== this.mainDetailsToggle) {
      this.closeSubmenu(openDetailsElement);
      return;
    }

    // focus can still sit on the trigger behind a panel that just opened
    const megaPanel =
      event.target.closest(".mega-submenu-mobile--visible") ||
      this.querySelector(".mega-submenu-mobile--visible");
    if (megaPanel) {
      megaPanel.querySelector(".js-mega-submenu-mobile-close-trigger")?.click();
      return;
    }

    if (openDetailsElement) this.closeMenuDrawer(this.openBtn);
  }

  // Nested levels are inline accordions inside the drawer (or a mega panel) that
  // the open trap already covers. Re-trapping on them would lock focus inside one
  // accordion and swallow taps on its sibling links.
  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;

    if (detailsElement === this.mainDetailsToggle) {
      super.onSummaryClick(event);
      return;
    }

    // read the state after the click settles: a click on the row's link or its
    // caret toggle reaches here too, and neither goes through the native toggle
    setTimeout(() => {
      summaryElement.setAttribute("aria-expanded", detailsElement.open);
    });
  }

  closeSubmenu(detailsElement) {
    if (detailsElement === this.mainDetailsToggle) {
      super.closeSubmenu(detailsElement);
      return;
    }

    const summaryElement = detailsElement.querySelector("summary");
    detailsElement.removeAttribute("open");
    summaryElement?.setAttribute("aria-expanded", false);
    (
      summaryElement?.querySelector(".js-submenu-toggle") || summaryElement
    )?.focus();
  }
}

if (!customElements.get("header-mobile-drawer")) {
  customElements.define("header-mobile-drawer", HeaderMobileDrawer);
}

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll(
      '[id^="ModalCloseBtn-"], [id^="ModalCloseOverlay-"]'
    ).forEach((el) => el.addEventListener("click", () => this.hide(false)));

    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        ) {
          this.hide();
        }
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    this.addEventListener(
      "transitionend",
      () => {
        if (this.hasAttribute("open")) {
          trapFocus(this, this.querySelector('[role="dialog"]'));
        }
      },
      { once: true }
    );
  }

  hide() {
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    document.body.classList.remove("overflow-hidden");
  }
}

if (!customElements.get("modal-dialog")) {
  customElements.define("modal-dialog", ModalDialog);
}

class ProductModal extends ModalDialog {
  constructor() {
    super();
  }
}

if (!customElements.get("product-modal")) {
  customElements.define("product-modal", ProductModal);
}

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}

if (!customElements.get("modal-opener")) {
  customElements.define("modal-opener", ModalOpener);
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  init() {
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (poster) {
      poster.addEventListener("click", this.loadContent.bind(this));
    }

    if (this.dataset.autoplay === "true") {
      this.loadContent();
    }
  }

  loadContent() {
    const parentEl =
      this.closest(".product__media") || this.closest(".product-media-modal");
    pauseAllMedia(parentEl);

    if (this.getAttribute("loaded")) return;

    const template = this.querySelector("template");
    if (!template) return;

    const content = document.createElement("div");
    content.appendChild(template.content.firstElementChild.cloneNode(true));
    this.setAttribute("loaded", true);

    const media = content.querySelector("video, model-viewer, iframe");
    if (!media) return;

    const deferredElement = this.appendChild(media);

    if (["VIDEO", "IFRAME"].includes(deferredElement.nodeName)) {
      const videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            let shouldAutoplay = false;
            const parentSlide = entry.target.closest(".swiper-slide");
            if (parentSlide) {
              if (parentSlide.classList.contains("swiper-slide-active")) {
                shouldAutoplay = true;
              }
            } else {
              if (entry.intersectionRatio > 0.7) {
                shouldAutoplay = true;
              }
            }

            if (shouldAutoplay) {
              if (
                (this.dataset.autoplay === "true" &&
                  !this.closest("product-modal")) ||
                (this.dataset.autoplay === "true" &&
                  this.closest("product-modal")?.hasAttribute("open"))
              ) {
                pauseAllMedia(parentEl, this);

                if (entry.target.nodeName === "VIDEO") {
                  entry.target.play()?.catch((error) => {
                    console.error("Error playing video:", error);
                  });
                } else if (
                  entry.target.nodeName === "IFRAME" &&
                  entry.target.contentWindow
                ) {
                  if (entry.target.classList.contains("js-youtube")) {
                    entry.target.contentWindow.postMessage(
                      '{"event":"command","func":"playVideo","args":""}',
                      "*"
                    );
                  } else {
                    entry.target.contentWindow.postMessage(
                      '{"method":"play"}',
                      "*"
                    );
                  }
                }
              }
            } else {
              if (entry.target.nodeName === "VIDEO") {
                entry.target.pause();
              } else if (
                entry.target.nodeName === "IFRAME" &&
                entry.target.contentWindow
              ) {
                if (entry.target.classList.contains("js-youtube")) {
                  entry.target.contentWindow.postMessage(
                    '{"event":"command","func":"pauseVideo","args":""}',
                    "*"
                  );
                } else {
                  entry.target.contentWindow.postMessage(
                    '{"method":"pause"}',
                    "*"
                  );
                }
              }
            }
          });
        },
        {
          threshold: Array.from({ length: 1000 }, (_, i) => i / 1000),
        }
      );

      videoObserver.observe(deferredElement);
    }

    const modelViewer = this.closest(".swiper-slide-active")?.querySelector(
      "model-viewer"
    );
    if (
      modelViewer &&
      !modelViewer.classList.contains("shopify-model-viewer-ui__disabled")
    ) {
      const swiper = this.closest(".swiper")?.swiper;
      if (swiper) {
        swiper.params.noSwiping = true;
        swiper.params.noSwipingClass = "swiper-slide";
      }
    }

    this.querySelectorAll(
      "video, .shopify-model-viewer-ui__controls-overlay"
    ).forEach((media) => {
      if (this.getAttribute("loaded") === "true") {
        media.addEventListener("click", (event) => {
          pauseAllMedia(parentEl, this);
        });
      }
    });
  }
}

if (!customElements.get("deferred-media")) {
  customElements.define("deferred-media", DeferredMedia);
}

class ProductModel extends DeferredMedia {
  constructor() {
    super();
  }

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: "model-viewer-ui",
        version: "1.0",
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  setupModelViewerUI(errors) {
    if (errors) return;

    this.modelViewerUI = new Shopify.ModelViewerUI(
      this.querySelector("model-viewer")
    );

    const $this = this;
    const target = $this.querySelector("model-viewer");

    const observer = new MutationObserver((records) => {
      if ($this.closest(".swiper") && $this.closest(".swiper").swiper) {
        const swiperObj = $this.closest(".swiper").swiper;
        if (!target.classList.contains("shopify-model-viewer-ui__disabled")) {
          swiperObj.params.noSwiping = true;
          swiperObj.params.noSwipingClass = "swiper-slide";
        } else {
          swiperObj.params.noSwiping = false;
        }
      }
    });

    observer.observe(target, { attributes: true });
  }
}

if (!customElements.get("product-model")) {
  customElements.define("product-model", ProductModel);
}

window.ProductModel = {
  loadShopifyXR() {
    Shopify.loadFeatures([
      {
        name: "shopify-xr",
        version: "1.0",
        onLoad: this.setupShopifyXR.bind(this),
      },
    ]);
  },

  setupShopifyXR(errors) {
    if (errors) return;

    if (!window.ShopifyXR) {
      document.addEventListener("shopify_xr_initialized", () =>
        this.setupShopifyXR()
      );
      return;
    }

    document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });
    window.ShopifyXR.setupXRElements();
  },
};

window.addEventListener("DOMContentLoaded", () => {
  window.ProductModel?.loadShopifyXR();
});

class VariantPicker extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.addEventListener("change", this.onVariantChange.bind(this));
    this.addEventListener("keydown", this.onKeyDown.bind(this));

    this.variantMediaDisplay =
      this.closest(".product")?.querySelector(".product__media")?.dataset
        ?.variantMediaDisplay || "show_all";
  }

  onVariantChange(event) {
    if (!this.contains(event.target)) return;

    const selectedValuesIds = this.getSelectedValuesIds();
    const requestUrl = this.createRequestUrl({
      selectedValuesIds: selectedValuesIds,
      connectedProductURL: this.connectedProductURL,
    });

    // One selection can announce itself twice - the dropdown dispatches its own change and
    // the radio it just moved fires a native one. Both resolve to the same request, so the
    // second would only abort the first and ask for the same HTML again.
    if (requestUrl && requestUrl === this.inFlightRequestUrl) return;
    this.inFlightRequestUrl = requestUrl;

    this.fetchProductInfo(requestUrl);

    this.resetErrorMessage();
  }

  // The option radios belong to the product form, where Enter would submit it and add the
  // item to the cart. Enter picks the focused value instead.
  onKeyDown(event) {
    if (event.defaultPrevented || event.key !== "Enter") return;
    if (event.target.type !== "radio") return;

    event.preventDefault();
    if (!event.target.checked) event.target.click();
  }

  // Rebuilding the option markup destroys the focused control and drops focus to <body>, so a
  // keyboard user would start over from the top of the page after every pick. Remember the
  // control here and hand focus to its counterpart once the new markup is in.
  getOptionFocus() {
    const activeEl = document.activeElement;
    const optionValuesEl = activeEl?.closest('[data-id^="OptionValues-"]');
    if (!optionValuesEl || !this.contains(optionValuesEl)) return null;

    let selector = ".dropdown-select__current";
    if (activeEl.id) {
      selector = `#${CSS.escape(activeEl.id)}`;
    } else if (activeEl.matches(".dropdown-select__option-notify")) {
      const value = activeEl.closest(".dropdown-select__item")?.dataset.value;
      selector = `.dropdown-select__item[data-value="${CSS.escape(value || "")}"] .dropdown-select__option-notify`;
    }

    return { position: optionValuesEl.dataset.optionPosition, selector };
  }

  restoreOptionFocus(optionFocus) {
    if (!optionFocus) return;
    if (document.activeElement && document.activeElement !== document.body) {
      return;
    }

    const optionValuesEl = this.querySelector(
      `[data-id^="OptionValues-"][data-option-position="${optionFocus.position}"]`
    );
    if (!optionValuesEl) return;

    // A radio inside a dropdown arrives with its panel closed and cannot take focus, so the
    // candidates run until one does: the same control, the checked value, the trigger
    const candidates = [
      optionValuesEl.querySelector(optionFocus.selector),
      optionValuesEl.querySelector("input[type='radio']:checked"),
      optionValuesEl.querySelector(".dropdown-select__current"),
    ];
    for (const el of candidates) {
      if (!el) continue;
      el.focus({ preventScroll: true });
      if (document.activeElement === el) return;
    }
  }

  getSelectedValuesIds() {
    const controls = [...this.querySelectorAll(".product-form__controls")];

    controls.sort((a, b) => {
      return (
        Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
      );
    });

    return controls.map((control) => {
      const checkedInput = control.querySelector('input[type="radio"]:checked');

      // Supporting combined listings: If the checked input has a connected product url, save it to use in the request
      if (checkedInput.dataset?.connectedProductUrl) {
        this.connectedProductURL = checkedInput.dataset.connectedProductUrl;
      }
      return checkedInput?.dataset?.optionValueId
        ? checkedInput.dataset.optionValueId
        : null;
    });
  }

  createRequestUrl({ selectedValuesIds = [], connectedProductURL = "" }) {
    const productUrl = connectedProductURL || `${this.dataset.url}`;
    const sectionId = this.dataset.originalSection
      ? this.dataset.originalSection
      : this.dataset.section;

    if (selectedValuesIds.length) {
      const params = [];
      params.push(`section_id=${sectionId}`);
      params.push(`option_values=${selectedValuesIds.join(",")}`);
      return `${productUrl}?${params.join("&")}`;
    }
  }

  fetchProductInfo(requestUrl) {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const requestController = this.abortController;

    this.toggleButtonsLoading(true);

    fetch(requestUrl, { signal: requestController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");

        try {
          // Search this.currentVariant
          this.setCurrentVariantAfterFetch(html);

          this.updateURL();
          this.updateVariantInput();
          this.updatePickupAvailability();

          if (
            this.currentVariant.featured_media &&
            this.variantMediaDisplay === "show_all"
          ) {
            // If variant display != "show_all", the media gallery element is fully replaced inside updateElementsAfterFetch
            const mediaId = `${this.dataset.section}-${this.currentVariant.featured_media.id}`;
            this.updateMedia(mediaId);
          }
        } catch (err) {}

        this.updateElementsAfterFetch(html);

        if (!this.currentVariant) {
          this.setUnavailable();
        } else {
          this.toggleAddButton(
            !this.currentVariant.available,
            window.variantStrings.soldOut
          );
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.info("Fetch aborted by user");
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        // a newer request already took over the loading state
        if (this.abortController === requestController) {
          this.inFlightRequestUrl = null;
          this.toggleButtonsLoading(false);
        }
      });
  }

  setCurrentVariantAfterFetch(html) {
    // attr data-original-section use for Quick view modal
    const sourceSectionId = this.dataset.originalSection
      ? this.dataset.originalSection
      : this.dataset.section;

    const variantPickerSource = html.querySelector(
      `[data-id="VariantPicker-${sourceSectionId}"]`
    );
    if (!variantPickerSource) return;

    const newVariantDataEl = variantPickerSource.querySelector(
      "[data-selected-variant]"
    );
    if (!newVariantDataEl) return;

    const newVariantData = variantPickerSource.querySelector(
      "[data-selected-variant]"
    ).innerHTML;

    const selectedVariant = !!newVariantData
      ? JSON.parse(newVariantData)
      : null;

    this.currentVariant = selectedVariant;
  }

  updateURL() {
    if (this.dataset.updateUrl === "false") return;
    const newUrl = this.currentVariant
      ? `${this.dataset.url}?variant=${this.currentVariant.id}`
      : this.dataset.url;

    window.history.replaceState({}, "", newUrl);
  }

  updateVariantInput() {
    if (!this.currentVariant?.id) return;

    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #sticky-bar-product-form-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event("change", { bubbles: true }));

      // Forms without a visible quantity selector (sticky bar / hidden selector)
      // carry a hidden quantity input — keep it at the variant's minimum
      // orderable quantity so add to cart passes the quantity rules.
      const hiddenQuantityInput = productForm.querySelector(
        'input[type="hidden"][name="quantity"]'
      );
      const quantityRule = this.currentVariant.quantity_rule;
      if (hiddenQuantityInput && quantityRule) {
        hiddenQuantityInput.value = Math.max(
          quantityRule.min || 1,
          quantityRule.increment || 1
        );
      }
    });

    publish(PUB_SUB_EVENTS.variantChange, {
      data: {
        sectionId: this.dataset.section,
        variant: this.currentVariant,
      },
    });

    // [Start theme custom event] - Variant change event
    document.dispatchEvent(
      new CustomEvent("variant:change", {
        detail: {
          variant: this.currentVariant,
          previousVariant: this.previousVariant || null,
          formElement: this,
          sectionId: this.dataset.section,
        },
      })
    );

    this.previousVariant = this.currentVariant;
    // [End theme custom event] - Variant change event
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelectorAll(
      `pickup-availability[data-section="${this.dataset.section}"]`
    );
    if (!pickUpAvailability.length) return;
    pickUpAvailability.forEach((el) => {
      if (this.currentVariant && this.currentVariant.available) {
        el.fetchAvailability(this.currentVariant.id);
      } else {
        el.removeAttribute("available");
        el.innerHTML = "";
      }
    });
  }

  updateMedia(mediaId) {
    if (!mediaId) return;

    // The quick view forces its own gallery layout, so it activates the media itself
    const quickViewModal = this.closest("quick-view-modal");
    if (quickViewModal) {
      if (typeof quickViewModal.setActiveMedia === "function") {
        quickViewModal.setActiveMedia(mediaId);
      }
      return;
    }

    const mediaEl = document.querySelector(
      `[data-section="product-media-${this.dataset.section}"]`
    );
    if (!mediaEl) return;

    // for slider and slider_previews layout
    const sliderEl = mediaEl.querySelector(".js-media-list");
    if (sliderEl && sliderEl.swiper) {
      const slideCount = sliderEl.querySelectorAll(".swiper-slide").length;
      const speed = slideCount > 32 ? 0 : 800;
      sliderEl.querySelectorAll(".swiper-slide").forEach((slide, index) => {
        const slideMediaId = slide.dataset?.mediaId;
        if (slideMediaId === mediaId) {
          const findIndex = sliderEl.swiper.slides.findIndex((slideEl) => {
            return slideEl.dataset?.mediaId === slideMediaId;
          });
          const fallbackIndex = Number(
            slide.dataset?.swiperSlideIndex || index
          );
          const swiperSlideIndex = findIndex !== -1 ? findIndex : fallbackIndex;
          sliderEl.swiper.slideTo(swiperSlideIndex, speed);
        }
      });
    }

    // for grid layout
    const gridEl =
      mediaEl.querySelector(".product__media-list[data-desktop-type='grid']") ||
      mediaEl.querySelector(".product__media-list[data-mobile-type='grid']");
    if (gridEl) {
      // nested media nodes carry their own data-media-id, so match items only
      gridEl
        .querySelectorAll("[data-media-id].product__media-item")
        .forEach((mediaItem) => {
          const mediaItemId = mediaItem.dataset?.mediaId;
          if (mediaItemId === mediaId) {
            mediaItem.classList.add("grid-active");
          } else {
            mediaItem.classList.remove("grid-active");
          }
        });
    }
  }

  updateElementsAfterFetch(html) {
    // attr data-original-section use for Quick view modal
    const currentSectionId = this.dataset.section;
    const sourceSectionId = this.dataset.originalSection
      ? this.dataset.originalSection
      : this.dataset.section;

    // Price
    const priceSource = html.querySelector(
      `[data-id="Price-${sourceSectionId}"]`
    );
    const priceDestination = document.querySelectorAll(
      `[data-id="Price-${currentSectionId}"]`
    );
    if (priceSource) {
      priceDestination.forEach((el) => {
        el.innerHTML = priceSource.innerHTML;
        el.classList.remove("visibility-hidden");
      });
    }

    // Badges: the Badges block and the gallery overlay both read the selected variant's
    // price and availability, so they follow the picker the same way the price does
    ["Badges", "MediaBadges"].forEach((key) => {
      const badgesSource = html.querySelector(
        `[data-id="${key}-${sourceSectionId}"]`
      );
      if (!badgesSource) return;
      document
        .querySelectorAll(`[data-id="${key}-${currentSectionId}"]`)
        .forEach((el) => {
          el.innerHTML = badgesSource.innerHTML;
        });
    });

    // Price in sticky add to cart bar
    const priceStickySource = html.querySelector(
      `[data-id="PriceSticky-${sourceSectionId}"]`
    );
    const priceStickyDestination = document.querySelectorAll(
      `[data-id="PriceSticky-${currentSectionId}"]`
    );
    if (priceStickySource) {
      priceStickyDestination.forEach((el) => {
        el.innerHTML = priceStickySource.innerHTML;
        el.classList.remove("visibility-hidden");
      });
    }

    // Media in sticky add to cart bar - the bar shows the variant's own image, so it
    // follows the picker the same way the price does
    const mediaStickySource = html.querySelector(
      `[data-id="MediaSticky-${sourceSectionId}"]`
    );
    const mediaStickyDestination = document.querySelectorAll(
      `[data-id="MediaSticky-${currentSectionId}"]`
    );
    if (mediaStickySource) {
      mediaStickyDestination.forEach((el) => {
        if (el.innerHTML !== mediaStickySource.innerHTML) {
          el.innerHTML = mediaStickySource.innerHTML;
        }
      });
    }

    // Inventory
    const inventorySource = html.querySelector(
      `[data-id="Inventory-${sourceSectionId}"]`
    );
    const inventoryDestination = document.querySelectorAll(
      `[data-id="Inventory-${currentSectionId}"]`
    );
    if (inventorySource) {
      inventoryDestination.forEach((el) => {
        el.innerHTML = inventorySource.innerHTML;
        el.classList.toggle(
          "visibility-hidden",
          inventorySource.innerText === ""
        );
      });
    }

    // SKU
    const skuSource = html.querySelector(`[data-id="Sku-${sourceSectionId}"]`);
    const skuDestination = document.querySelectorAll(
      `[data-id="Sku-${currentSectionId}"]`
    );
    if (skuSource) {
      skuDestination.forEach((el) => {
        el.innerHTML = skuSource.innerHTML;
        el.classList.toggle(
          "visibility-hidden",
          skuSource.classList.contains("visibility-hidden")
        );
      });
    }

    // Variant picker: Color swatches label
    const colorNameSources = html.querySelectorAll(
      `[data-id^="ColorName-${sourceSectionId}"]`
    );
    const colorNameDestinations = document.querySelectorAll(
      `[data-id^="ColorName-${currentSectionId}"]`
    );
    if (colorNameSources?.length === colorNameDestinations?.length) {
      colorNameDestinations.forEach((colorNameDestination, index) => {
        colorNameDestination.classList.toggle(
          "visibility-hidden",
          colorNameSources[index].classList.contains("visibility-hidden")
        );
        colorNameDestination.innerHTML = colorNameSources[index].innerHTML;
      });
    }

    // Variant picker: Options
    const variantPickerSource = html.querySelector(
      `[data-id="VariantPicker-${sourceSectionId}"]`
    );
    const variantPickerDestinations = document.querySelectorAll(
      `[data-id="VariantPicker-${currentSectionId}"]`
    );
    const optionFocus = this.getOptionFocus();
    if (variantPickerSource) {
      const quickViewModal = this.closest("quick-view-modal");
      const sourceOptionValues = variantPickerSource.querySelectorAll(
        `[data-id^="OptionValues-${sourceSectionId}-"]`
      );
      const sourceCurrentVariantScript = variantPickerSource.querySelector(
        "[data-selected-variant]"
      );

      if (sourceOptionValues.length && !this.connectedProductURL) {
        variantPickerDestinations.forEach((pickerDestEl) => {
          sourceOptionValues.forEach((sourceOptionsEl) => {
            const position = sourceOptionsEl.dataset.optionPosition;
            if (!position) return;
            const destOptionsEl = pickerDestEl.querySelector(
              `[data-option-position="${position}"][data-id^="OptionValues-"]`
            );
            if (destOptionsEl) {
              destOptionsEl.innerHTML = quickViewModal
                ? sourceOptionsEl.innerHTML.replaceAll(
                    sourceSectionId,
                    `quickview-${sourceSectionId}`
                  )
                : sourceOptionsEl.innerHTML;
            }
          });

          if (sourceCurrentVariantScript) {
            const destCurrentVariantScript = pickerDestEl.querySelector(
              "[data-selected-variant]"
            );
            if (destCurrentVariantScript) {
              destCurrentVariantScript.innerHTML =
                sourceCurrentVariantScript.innerHTML;
            }
          }
        });
      } else {
        // fallback to replace whole variant picker html
        if (quickViewModal) {
          variantPickerDestinations.forEach((el) => {
            el.innerHTML = variantPickerSource.innerHTML.replaceAll(
              sourceSectionId,
              `quickview-${sourceSectionId}`
            );
          });
        } else {
          variantPickerDestinations.forEach((el) => {
            el.innerHTML = variantPickerSource.innerHTML;
          });
        }
      }

      this.restoreOptionFocus(optionFocus);
    }

    // Update elements for B2B quantity pricing https://help.shopify.com/en/manual/b2b/catalogs/quantity-pricing
    const productButtonsSource = html.querySelector(
      `[data-id="ProductButtons-${sourceSectionId}"]`
    );
    const productButtonsDestination = document.querySelectorAll(
      `[data-id="ProductButtons-${currentSectionId}"]`
    );
    if (productButtonsSource) {
      const quickViewModal = this.closest("quick-view-modal");
      const sourceButtonsWrapper = productButtonsSource.querySelector(
        ".product-form__buttons"
      );
      productButtonsDestination.forEach((el) => {
        const buttonsWrapper = el.querySelector(".product-form__buttons");

        if (sourceButtonsWrapper && buttonsWrapper) {
          const sourceHasQuantityPricing =
            sourceButtonsWrapper.dataset.hasQuantityPricing === "true";
          const destHadQuantityPricing =
            buttonsWrapper.dataset.hasQuantityPricing === "true";

          // This function is executed if quantity pricing is configured for the product or product variant
          if (sourceHasQuantityPricing || destHadQuantityPricing) {
            if (sourceHasQuantityPricing) {
              buttonsWrapper.dataset.hasQuantityPricing = "true";
            } else {
              buttonsWrapper.removeAttribute("data-has-quantity-pricing");
            }

            // Update b2b elements (volume pricing + quantity rules)
            const sourceB2bEl = sourceButtonsWrapper.querySelector(
              ".product-b2b-elements"
            );
            const destB2bEl = buttonsWrapper.querySelector(
              ".product-b2b-elements"
            );
            if (sourceB2bEl && destB2bEl) {
              destB2bEl.innerHTML = quickViewModal
                ? sourceB2bEl.innerHTML.replaceAll(
                    sourceSectionId,
                    `quickview-${sourceSectionId}`
                  )
                : sourceB2bEl.innerHTML;
            } else if (sourceB2bEl && !destB2bEl) {
              const b2bHTML = quickViewModal
                ? sourceB2bEl.outerHTML.replaceAll(
                    sourceSectionId,
                    `quickview-${sourceSectionId}`
                  )
                : sourceB2bEl.outerHTML;
              buttonsWrapper.insertAdjacentHTML("afterbegin", b2bHTML);
            } else if (!sourceB2bEl && destB2bEl) {
              destB2bEl.remove();
            }

            // Update quantity input
            const sourceQuantityWrapper = sourceButtonsWrapper.querySelector(
              ".product-form__quantity"
            );
            const destQuantityWrapper = buttonsWrapper.querySelector(
              ".product-form__quantity"
            );
            if (sourceQuantityWrapper && destQuantityWrapper) {
              destQuantityWrapper.innerHTML = quickViewModal
                ? sourceQuantityWrapper.innerHTML.replaceAll(
                    sourceSectionId,
                    `quickview-${sourceSectionId}`
                  )
                : sourceQuantityWrapper.innerHTML;
            }
          }
        }
      });
    }

    // Product media
    // Product media is replaced with DOM changes and re-initialization of initProductMediaGallery only when Variant media display is not set to "Show all" or when a product is linked to the variant (e.g., via Combined listings).
    // In most other cases, it updates via updateMedia without DOM changes.
    if (this.variantMediaDisplay !== "show_all" || this.connectedProductURL) {
      const mediaSource = html.querySelector(
        `[data-section="product-media-${sourceSectionId}"]`
      );
      const mediaDestination = document.querySelector(
        `[data-section="product-media-${currentSectionId}"]`
      );
      if (mediaSource && mediaDestination) {
        mediaDestination.innerHTML = mediaSource.innerHTML;

        const parentQuickView = this.closest("quick-view-modal");
        if (parentQuickView) {
          if (typeof parentQuickView.removeDOMElements === "function") {
            parentQuickView.removeDOMElements(mediaDestination);
          }
          if (typeof parentQuickView.initSlider === "function") {
            parentQuickView.initSlider();
          }
        } else {
          // Update zoom modal for Combined listings that was moved to body by ModalDialog.connectedCallback
          if (this.connectedProductURL) {
            const zoomModalSource = html.querySelector(
              `#ProductModal-${sourceSectionId}`
            );
            const zoomModalDest = document.querySelector(
              `#ProductModal-${currentSectionId}`
            );

            if (zoomModalSource && zoomModalDest) {
              const newZoomModal = zoomModalSource.cloneNode(true);
              newZoomModal.id = `ProductModal-${currentSectionId}`;
              zoomModalDest.replaceWith(newZoomModal);
            }
          }

          const section = document.getElementById(
            `shopify-section-${currentSectionId}`
          );

          if (section && typeof window.initProductMediaGallery === "function") {
            window.initProductMediaGallery(section);
          }
        }
      }
    }

    // Replace other elements for Combined listings
    if (this.connectedProductURL) {
      // ...Src - source elements from response HTML
      // ...Dest - destination elements in the current DOM
      const productInfoSrc = html.getElementById(
        `ProductInfo-${sourceSectionId}`
      );
      const productInfoDest = document.getElementById(
        `ProductInfo-${currentSectionId}`
      );
      if (productInfoSrc && productInfoDest) {
        // Title
        const titleSrc = productInfoSrc.querySelector(".product__title");
        const titleDest = productInfoDest.querySelectorAll(".product__title");
        if (titleSrc) {
          titleDest.forEach((el) => {
            el.innerHTML = titleSrc.innerHTML;
          });
        }

        // Vendor
        const vendorSrc = productInfoSrc.querySelector(".product__vendor");
        const vendorDest = productInfoDest.querySelectorAll(".product__vendor");
        if (vendorSrc) {
          vendorDest.forEach((el) => {
            el.innerHTML = vendorSrc.innerHTML;
          });
        }

        // Description
        const descriptionSrc = productInfoSrc.querySelector(
          ".product__description"
        );
        const descriptionDest = productInfoDest.querySelectorAll(
          ".product__description"
        );
        if (descriptionSrc) {
          descriptionDest.forEach((el) => {
            el.innerHTML = descriptionSrc.innerHTML;
            el.classList.toggle(
              "hidden",
              descriptionSrc.classList.contains("hidden")
            );
          });
        }
      }

      // Complementary products
      const complementaryProductsSrc = productInfoSrc.querySelector(
        "product-recommendations.complementary-products"
      );
      const complementaryProductsDest = productInfoDest.querySelectorAll(
        "product-recommendations.complementary-products"
      );
      if (complementaryProductsSrc) {
        complementaryProductsDest.forEach((el) => {
          el.replaceWith(complementaryProductsSrc.cloneNode(true));
        });
      }
    }
  }

  toggleAddButton(disable = true, text) {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #sticky-bar-product-form-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      // The dynamic checkout button follows the Add to cart button: a sold-out or
      // unavailable variant has nothing to check out with, so it leaves the form
      // rather than sitting there disabled but looking clickable
      const dynamicCheckout = productForm.querySelector(
        ".product-form__checkout"
      );
      dynamicCheckout?.toggleAttribute("hidden", disable);

      const addButton = productForm.querySelector('[name="add"]');
      if (!addButton) return;

      const addButtonText = addButton.querySelector(".js-change-button-text");

      if (disable) {
        addButton.setAttribute("disabled", true);
        addButton.setAttribute("aria-disabled", true);
        if (text && addButtonText) {
          addButtonText.textContent = text;

          if (text === window.variantStrings.unavailable) {
            addButton.dataset.status = "unavailable";
          } else {
            addButton.dataset.status = "sold-out";
          }
        }
      } else {
        addButton.removeAttribute("disabled");
        addButton.removeAttribute("aria-disabled");
        if (addButtonText) {
          addButtonText.textContent = window.variantStrings.addToCart;
        }
        addButton.dataset.status = "available";
      }
    });
  }

  toggleButtonsLoading(loading) {
    const buttonsWrapper = this.closest(".product")?.querySelectorAll(
      `[data-id="ProductButtons-${this.dataset.section}"]`
    );
    buttonsWrapper?.forEach((el) => {
      el.classList.toggle("loading", loading);
    });
  }

  setUnavailable() {
    const price = document.querySelectorAll(
      `[data-id="Price-${this.dataset.section}"]`
    );
    const priceSticky = document.querySelectorAll(
      `[data-id="PriceSticky-${this.dataset.section}"]`
    );
    const inventory = document.querySelectorAll(
      `[data-id="Inventory-${this.dataset.section}"]`
    );
    const sku = document.querySelectorAll(
      `[data-id="Sku-${this.dataset.section}"]`
    );
    const colorName = document.querySelectorAll(
      `[data-id^="ColorName-${this.dataset.section}"]`
    );

    this.toggleAddButton(true, window.variantStrings.unavailable);

    price.forEach((el) => el.classList.add("visibility-hidden"));
    priceSticky.forEach((el) => el.classList.add("visibility-hidden"));
    inventory.forEach((el) => el.classList.add("visibility-hidden"));
    sku.forEach((el) => el.classList.add("visibility-hidden"));
    colorName.forEach((el) => el.classList.add("visibility-hidden"));
  }

  resetErrorMessage() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #sticky-bar-product-form-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const parentEl =
        productForm.closest(".product-sticky-add-bar") ||
        productForm.closest("product-form");
      if (parentEl) {
        const errorWrapperEl = parentEl.querySelector(
          ".product-form__error-message-wrapper"
        );
        const errorTextEl = errorWrapperEl?.querySelector(
          ".product-form__error-message"
        );
        if (!errorWrapperEl || !errorTextEl) return;
        errorWrapperEl.setAttribute("hidden", true);
        errorTextEl.textContent = "";
      }
    });
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}

// Gap the panel keeps from the edge of its clipping box, and the smallest panel worth
// opening (about three option rows) when neither side has enough room.
const DROPDOWN_PANEL_MARGIN = 8;
const DROPDOWN_PANEL_MIN_HEIGHT = 138;

class VariantDropdownSelect extends HTMLElement {
  constructor() {
    super();

    this.currentEl = this.querySelector(".dropdown-select__current");
    this.optionsWrapperEl = this.querySelector(".dropdown-select__options");
    this.hiddenInput = this.querySelector("input[type='hidden']");
    if (!this.currentEl || !this.optionsWrapperEl || !this.hiddenInput) return;

    this.optionsEls = Array.from(this.optionsWrapperEl.querySelectorAll("li"));
    this.inputs = Array.from(this.optionsWrapperEl.querySelectorAll("input"));
    this.hasColorSwatch = this.currentEl?.classList?.contains(
      "dropdown-select__current--with-color"
    );
    // A required dropdown pairs the hidden input with a real <select> the browser can
    // validate (the hidden input is barred from constraint validation); it mirrors the
    // value so an empty required field blocks the submit natively
    this.validationProxy = this.querySelector("select[data-validation-proxy]");
    this.isActive = false;

    this.optionPosition = this.getOptionPosition();
    this._onCurrentClick = this.onCurrentClick.bind(this);
    this._onCurrentKeyDown = this.onCurrentKeyDown.bind(this);
    this._onOptionsClick = this.onClickOption.bind(this);
    this._onOptionsKeyDown = this.onOptionsKeyDown.bind(this);
    this._onKeyUp = this.onKeyUp.bind(this);
    this._onFocusOut = this.onFocusOut.bind(this);
    this._onOutsideClick = this.onOutsideClick.bind(this);
    this._onProxyInvalid = this.onProxyInvalid.bind(this);
  }

  connectedCallback() {
    this.currentEl.addEventListener("click", this._onCurrentClick);
    this.currentEl.addEventListener("keydown", this._onCurrentKeyDown);
    this.currentEl.addEventListener("dblclick", this.preventDoubleClick);
    this.optionsWrapperEl.addEventListener("click", this._onOptionsClick);
    this.optionsWrapperEl.addEventListener("keydown", this._onOptionsKeyDown);
    this.addEventListener("keyup", this._onKeyUp);
    this.addEventListener("focusout", this._onFocusOut);
    document.addEventListener("click", this._onOutsideClick);
    this.validationProxy?.addEventListener("invalid", this._onProxyInvalid);
    this.syncFilledState();
  }

  disconnectedCallback() {
    this.currentEl.removeEventListener("click", this._onCurrentClick);
    this.currentEl.removeEventListener("keydown", this._onCurrentKeyDown);
    this.currentEl.removeEventListener("dblclick", this.preventDoubleClick);
    this.optionsWrapperEl.removeEventListener("click", this._onOptionsClick);
    this.optionsWrapperEl.removeEventListener(
      "keydown",
      this._onOptionsKeyDown
    );
    this.removeEventListener("keyup", this._onKeyUp);
    this.removeEventListener("focusout", this._onFocusOut);
    document.removeEventListener("click", this._onOutsideClick);
    this.validationProxy?.removeEventListener("invalid", this._onProxyInvalid);
  }

  // The .dropdown-select--field-floating label doubles as the placeholder, so the
  // trigger has to say whether it carries a value
  syncFilledState() {
    if (!this.hiddenInput) return;

    const value = this.hiddenInput.value;
    this.classList.toggle("is-filled", Boolean(value));

    if (!this.validationProxy) return;
    this.validationProxy.value = value;
    if (value) this.classList.remove("is-invalid");
  }

  // The proxy is invisible, so the trigger carries the error state
  onProxyInvalid() {
    this.classList.add("is-invalid");
  }

  onCurrentClick(event) {
    event.stopPropagation();
    if (this.isActive) {
      this.onClose();
    } else {
      this.onOpen();
    }
  }

  onCurrentKeyDown(event) {
    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        if (this.isActive) {
          this.onClose();
        } else {
          this.onOpen();
        }
        break;
      case "ArrowDown":
      case "ArrowUp":
        event.preventDefault();
        if (this.isActive) {
          this.focusSelectedItem();
        } else {
          this.onOpen();
        }
        break;
    }
  }

  // Every option and every Notify me button in the open panel is one stop, in list order,
  // for Tab and the arrow keys alike. Moving focus never picks a value — Enter / Space does —
  // so a keyboard user can walk the list before choosing.
  onOptionsKeyDown(event) {
    const items = this.getPanelItems();
    const index = items.indexOf(event.target);
    if (index === -1) return;

    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
      case "ArrowLeft":
      case "ArrowRight":
        // A radio natively checks its neighbour on any arrow, which would pick a value
        event.preventDefault();
        if (event.key === "ArrowDown") {
          items[Math.min(index + 1, items.length - 1)].focus();
        } else if (event.key === "ArrowUp") {
          items[Math.max(index - 1, 0)].focus();
        }
        break;
      case "Home":
      case "End":
        event.preventDefault();
        items[event.key === "Home" ? 0 : items.length - 1].focus();
        break;
      case "Tab":
        if (event.shiftKey) {
          event.preventDefault();
          if (index === 0) {
            this.onClose();
            this.currentEl.focus();
          } else {
            items[index - 1].focus();
          }
        } else if (index < items.length - 1) {
          event.preventDefault();
          items[index + 1].focus();
        }
        // Tab past the last item moves on to the page, and focusout closes the panel
        break;
      case "Enter":
        // The radio belongs to a form, where Enter would submit it; buttons keep theirs
        if (event.target.type !== "radio") return;
        event.preventDefault();
        event.target.click();
        break;
    }
  }

  // Escape is handled on keyup and stopped here: a modal around the dropdown closes on the
  // same keyup and would otherwise close along with the panel
  onKeyUp(event) {
    if (event.key !== "Escape" || !this.isActive) return;

    event.preventDefault();
    event.stopPropagation();
    this.onClose();
    this.currentEl.focus();
  }

  onFocusOut(event) {
    if (!this.isActive || this.contains(event.relatedTarget)) return;
    this.onClose();
  }

  getPanelItems() {
    return Array.from(
      this.optionsWrapperEl.querySelectorAll(
        "input[type='radio'], button:enabled"
      )
    );
  }

  focusSelectedItem() {
    const selected =
      this.inputs.find((input) => input.checked) || this.inputs[0];
    selected?.focus();
  }

  preventDoubleClick(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  onClickOption(event) {
    const optionEl = event.target.closest("li");
    const currentValueEl = this.currentEl.querySelector(
      "[data-dropdown-current-value]"
    );

    if (
      !optionEl ||
      !currentValueEl ||
      !this.optionsWrapperEl.contains(optionEl)
    ) {
      event.preventDefault();
      return;
    }

    const newValue = optionEl.dataset.value;
    const optionInput = optionEl.querySelector("input[type='radio']");
    // Clicking the option's label makes the browser forward a second click to the radio it
    // labels, and both reach this delegated listener. The first run already moved the
    // selection, so only a real change is announced - announcing twice sent the picker's
    // whole variant request twice.
    const isNewSelection = this.hiddenInput.value !== newValue;

    currentValueEl.textContent = newValue;
    this.hiddenInput.value = newValue;
    this.syncFilledState();

    if (this.hasColorSwatch) {
      const newColor = optionEl.dataset.color;
      this.hiddenInput.dataset.colorSwatch = newColor;
      this.currentEl.style.setProperty("--swatch-color", newColor);
    }

    // A label click checks the radio natively, but a click on a control beside it (Notify me)
    // does not — and the picker reads the selection off the checked radio.
    if (optionInput) {
      optionInput.checked = true;
    }
    this.inputs.forEach((el) => el.removeAttribute("checked"));
    optionInput?.setAttribute("checked", "true");

    if (isNewSelection) {
      this.hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    this.onClose();
    this.currentEl.focus();
  }

  onOpen() {
    document.querySelectorAll("variant-dropdown-select").forEach((dropdown) => {
      if (dropdown !== this && typeof dropdown.onClose === "function") {
        dropdown.onClose();
      }
    });

    this.placePanel();
    this.optionsWrapperEl.classList.add("active");
    this.currentEl.setAttribute("aria-expanded", "true");
    this.isActive = true;

    this.focusSelectedItem();
  }

  onClose() {
    // The flip and the height cap are left standing. The panel fades out, and neither its
    // anchor nor its max-height animates, so clearing them here drops the still-visible
    // panel to the other side of the trigger for the length of the transition. placePanel
    // decides both again on every open.
    this.optionsWrapperEl.classList.remove("active");
    this.currentEl.setAttribute("aria-expanded", "false");
    this.isActive = false;
  }

  // The panel is absolutely positioned, so a trigger low in a clipped scroll container
  // (the quick view panel, a long product column) would open into space that is cut
  // off. Open upwards when that side has more room, and cap the list to what is left.
  placePanel() {
    const list = this.optionsWrapperEl.querySelector(".dropdown-select__list");
    if (!list) return;

    // Measured uncapped: a cap left over from the previous open under-reports the height
    // the panel wants and can flip it to the wrong side.
    this.style.removeProperty("--dropdown-select-room");

    const bounds = this.getClipBounds();
    const trigger = this.currentEl.getBoundingClientRect();
    const below = bounds.bottom - trigger.bottom - DROPDOWN_PANEL_MARGIN;
    const above = trigger.top - bounds.top - DROPDOWN_PANEL_MARGIN;
    const wanted = this.optionsWrapperEl.offsetHeight || list.scrollHeight;

    const flipUp = below < wanted && above > below;
    this.classList.toggle("dropdown-select--up", flipUp);

    const room = Math.max(flipUp ? above : below, DROPDOWN_PANEL_MIN_HEIGHT);
    if (room < wanted) {
      this.style.setProperty("--dropdown-select-room", `${Math.round(room)}px`);
    } else {
      this.style.removeProperty("--dropdown-select-room");
    }
  }

  // The visible band left by every scrolling / clipping ancestor, viewport included
  getClipBounds() {
    const bounds = { top: 0, bottom: window.innerHeight };
    let el = this.parentElement;

    while (el && el !== document.body) {
      const overflowY = window.getComputedStyle(el).overflowY;
      if (overflowY !== "visible") {
        const rect = el.getBoundingClientRect();
        bounds.top = Math.max(bounds.top, rect.top);
        bounds.bottom = Math.min(bounds.bottom, rect.bottom);
      }
      el = el.parentElement;
    }

    return bounds;
  }

  onOutsideClick(event) {
    if (!this.isActive || this.contains(event.target)) return;
    this.onClose();
  }

  getOptionPosition() {
    const parentFieldset = this.closest(".product-form__controls");
    return Number(parentFieldset?.dataset?.optionPosition || -1);
  }

  updateCurrentOption(selectedValues = []) {
    // Method is used to synchronize after changes options in sticky bar.
    if (this.optionPosition === -1) return;
    const currentValueEl = this.currentEl.querySelector(
      "[data-dropdown-current-value]"
    );

    if (!currentValueEl || !selectedValues || !selectedValues.length) return;

    const newValue = selectedValues[this.optionPosition - 1];

    currentValueEl.textContent = newValue;
    this.hiddenInput.value = newValue;
    this.syncFilledState();

    this.optionsEls.forEach((optionEl) => {
      const liDataValue = optionEl.dataset.value;
      if (newValue === liDataValue && this.hasColorSwatch) {
        const newColor = optionEl.dataset.color;
        this.hiddenInput.dataset.colorSwatch = newColor;
        this.currentEl.style.setProperty("--swatch-color", newColor);
      }
    });
  }
}

if (!customElements.get("variant-dropdown-select")) {
  customElements.define("variant-dropdown-select", VariantDropdownSelect);
}

// How long the outgoing block needs to clear the icon group viewport
const ICON_GROUP_EXIT_DURATION = 400;

// Icon with text group: "Enable animation" rotates the child blocks inside the clipped
// viewport - the next one rolls up from the bottom, the outgoing one leaves through the
// top (the announcement-bar message contract). A custom element, so the product page, a
// featured product and the quick view all start and stop it without an init call.
class ProductIconGroup extends HTMLElement {
  connectedCallback() {
    if (!this.classList.contains("product-icon-group--animated")) return;

    const viewport = this.querySelector(".product-icon-group__viewport");
    this.items = viewport
      ? Array.from(viewport.children).filter((child) => child.nodeType === 1)
      : [];
    if (!this.items.length) return;

    this.stop();
    this.index = 0;
    this.items.forEach((item, itemIndex) => {
      item.classList.remove("is-exit");
      item.classList.toggle("is-active", itemIndex === 0);
    });
    this.classList.add("is-ready");

    if (this.items.length < 2) return;

    const duration = (parseFloat(this.dataset.groupAutoplay) || 3) * 1000;
    this.timer = window.setInterval(() => {
      this.show((this.index + 1) % this.items.length);
    }, duration);
  }

  disconnectedCallback() {
    this.stop();
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    if (this.exitTimer) window.clearTimeout(this.exitTimer);
    this.timer = null;
    this.exitTimer = null;
  }

  show(next) {
    const current = this.items[this.index];
    const upcoming = this.items[next];
    if (!upcoming) return;

    if (this.exitTimer) {
      window.clearTimeout(this.exitTimer);
      this.exitTimer = null;
    }
    this.items.forEach((item) => item.classList.remove("is-exit"));

    if (current && current !== upcoming) {
      current.classList.remove("is-active");
      current.classList.add("is-exit");
      this.exitTimer = window.setTimeout(() => {
        current.classList.remove("is-exit");
        this.exitTimer = null;
      }, ICON_GROUP_EXIT_DURATION);
    }

    upcoming.classList.add("is-active");
    this.index = next;
  }
}

if (!customElements.get("product-icon-group")) {
  customElements.define("product-icon-group", ProductIconGroup);
}

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    if (this.querySelector("form")) {
      this.form = this.querySelector("form");
      this.form.querySelector("[name=id]").disabled = false;
      this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
    } else {
      if (this.querySelector("[name=id]")) {
        this.querySelector("[name=id]").disabled = false;
      }
      this.querySelector("button[type=submit]")?.addEventListener(
        "click",
        this.onSubmitHandler.bind(this)
      );
    }

    this.cartUIElement =
      this.closest("cart-drawer") ||
      document.querySelector("cart-notification") ||
      document.querySelector("cart-drawer");
    this.submitButton = this.querySelector('[type="submit"]');
    if (this.cartUIElement) {
      this.submitButton.setAttribute("aria-haspopup", "dialog");
    }

    this.hideErrors = this.dataset.hideErrors === "true";
  }

  // The sticky bar posts its own bare form, so the line item properties - they live in the
  // main form and reach it only through the `form` attribute - are neither validated nor
  // sent on that path. Borrow the form that owns them.
  mainFormForStickyBar() {
    const stickyPrefix = "sticky-bar-product-form-";
    // getAttribute, not .id - the form owns an `id` input, and that named property
    // shadows the element's own id
    const formId = this.form?.getAttribute("id") || "";
    if (!formId.startsWith(stickyPrefix)) return null;

    return document.getElementById(
      `product-form-${formId.slice(stickyPrefix.length)}`
    );
  }

  propertyFieldsOf(form) {
    return Array.from(form.elements).filter(
      (element) => !element.disabled && element.name?.startsWith("properties[")
    );
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (this.submitButton.getAttribute("aria-disabled") === "true") return;

    const stickyMainForm = this.mainFormForStickyBar();
    if (stickyMainForm) {
      const unfilled = this.propertyFieldsOf(stickyMainForm).find(
        (element) => !element.checkValidity()
      );
      if (unfilled) {
        unfilled.scrollIntoView({ block: "center" });
        unfilled.reportValidity();
        return;
      }
    }

    this.handleErrorMessage();

    this.submitButton.classList.add("loading");
    const btnSpinner =
      this.submitButton.querySelector(".button-loader") ||
      this.submitButton.querySelector(".loading-overlay__spinner");
    if (btnSpinner) btnSpinner.classList.remove("hidden");

    const config = fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];

    const formData = new FormData(this.form);
    if (!this.form) {
      formData.append("id", this.querySelector("[name=id]").value);
    }

    if (stickyMainForm) {
      this.propertyFieldsOf(stickyMainForm).forEach((element) => {
        const isToggle =
          element.type === "checkbox" || element.type === "radio";
        if (isToggle && !element.checked) return;
        formData.append(element.name, element.value);
      });
    }

    // A blank property is still hashed into the line item key, so an untouched optional
    // field would split the variant off from the sticky bar's add, which carries none
    Array.from(formData.entries()).forEach(([key, value]) => {
      if (!key.startsWith("properties[")) return;
      if (typeof value === "string" && value.trim() === "") {
        formData.delete(key);
      }
    });

    if (this.cartUIElement) {
      formData.append(
        "sections",
        this.cartUIElement
          .getSectionsToRender()
          .map((section) => section.section)
      );
      formData.append("sections_url", window.location.pathname);
    }
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then(parseJsonResponse)
      .then((response) => {
        // If there is a status in the response, then this is an error
        // https://shopify.dev/docs/api/ajax/reference/cart#error-responses
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: "product-form",
            productVariantId: formData.get("id"),
            errors: response.description,
            message: response.message,
          });

          // [Start theme custom event] - Cart error event
          document.dispatchEvent(
            new CustomEvent("cart:error", {
              detail: {
                errors: response.description,
                message: response.message,
              },
            })
          );
          const refreshCartUIEvent = new CustomEvent("cart:refresh");
          document.dispatchEvent(refreshCartUIEvent);
          // [End theme custom event] - Cart error event

          this.handleErrorMessage(response.description);
          const soldOutMessage =
            this.submitButton.querySelector(".sold-out-message");
          if (!soldOutMessage) return;
          this.submitButton.setAttribute("aria-disabled", true);
          this.submitButton.querySelector("span").classList.add("hidden");
          soldOutMessage.classList.remove("hidden");
          this.error = true;
          return;
        } else if (!this.cartUIElement) {
          window.location = window.routes.cart_url;
          return;
        }

        if (!this.error) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: formData.get("id"),
            // renderContents() below already rebuilds both drawer surfaces from this
            // add response, so the drawer must not fetch the same section again.
            cartUIRendered: this.cartUIElement?.tagName === "CART-DRAWER",
          });
        }
        this.error = false;

        // [Start theme custom event] - Variant addition event
        document.dispatchEvent(
          new CustomEvent("variant:add", {
            detail: {
              variant: {
                id: response.variant_id || formData.get("id"),
                quantity:
                  response.quantity || Number(formData.get("quantity") || 1),
                product_title: response.product_title || "",
                variant_title: response.variant_title || "",
                image: response.image || null,
              },
              formElement: this.form,
            },
          })
        );
        // [End theme custom event] - Variant addition event

        // [Start theme custom event] - Cart change event
        fetch(`${routes.cart_url}.js`)
          .then(parseJsonResponse)
          .then((parsedState) => {
            document.dispatchEvent(
              new CustomEvent("cart:change", {
                detail: {
                  cart: parsedState,
                },
              })
            );
          })
          .catch((error) => {
            console.error("Error fetching cart state:", error);
          });
        // [End theme custom event] - Cart change event

        let activeElement = document.activeElement;

        // add to cart from quick-view modal
        const quickAddModal = this.closest("quick-view-modal");
        if (quickAddModal && typeof quickAddModal?.hide === "function") {
          activeElement = quickAddModal.openedBy;
          document.body.addEventListener(
            "quick-view-closed",
            () => {
              setTimeout(() => {
                this.cartUIElement.setActiveElement(activeElement);
                this.cartUIElement.renderContents(response);
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
          return;
        }

        this.cartUIElement.setActiveElement(activeElement);
        this.cartUIElement.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        if (this.cartUIElement?.closest("cart-notification")) {
          const cartDrawer = document.querySelector("cart-drawer");
          if (cartDrawer && cartDrawer.classList.contains("is-empty")) {
            cartDrawer.classList.remove("is-empty");
          }
        }

        if (
          this.cartUIElement?.closest("cart-drawer") &&
          this.cartUIElement?.classList.contains("is-empty")
        ) {
          this.cartUIElement.classList.remove("is-empty");
        }
        if (!this.error) this.submitButton.removeAttribute("aria-disabled");
        this.submitButton.classList.remove("loading");
        if (btnSpinner) btnSpinner.classList.add("hidden");
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    const stickyBarParent = this.closest(".product-sticky-add-bar");
    const horizontalProductParent = this.closest(".product-card-horizontal");
    const errorWrapperSelector = ".product-form__error-message-wrapper";

    this.errorMessageWrapper =
      this.errorMessageWrapper ||
      this.querySelector(errorWrapperSelector) ||
      stickyBarParent?.querySelector(errorWrapperSelector) ||
      horizontalProductParent?.querySelector(errorWrapperSelector);
    if (!this.errorMessageWrapper) return;

    this.errorMessage =
      this.errorMessage ||
      this.errorMessageWrapper.querySelector(".product-form__error-message");
    if (!this.errorMessage) return;

    if (this.errorTimeoutId) {
      clearTimeout(this.errorTimeoutId);
      this.errorTimeoutId = null;
    }

    this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;

      // Auto-hide error message after 3 seconds for product-card-horizontal
      if (horizontalProductParent) {
        this.errorTimeoutId = setTimeout(() => {
          this.errorMessageWrapper.toggleAttribute("hidden", true);
          this.errorMessage.textContent = "";
          this.errorTimeoutId = null;
        }, 3000);
      }
    }
  }
}

if (!customElements.get("product-form")) {
  customElements.define("product-form", ProductForm);
}

class ProductCard extends HTMLElement {
  // Use this class to define product card-specific functionality, such as slider initialization, variant and swatch switching, and more.
  constructor() {
    super();
  }
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.recommendationsSlider = null;
    this.autoplayObserver = null;
  }

  connectedCallback() {
    if (this._initialized) return;
    this.initProductRecommendations();
  }

  disconnectedCallback() {
    this.destroyComplementarySlider();
  }

  initProductRecommendations() {
    this.enableLoading();

    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");
        // Nothing to read the attributes off: complementary products can resolve to
        // an empty answer, and the endpoint 404s while a product is being set up
        if (!recommendations) return;

        if (recommendations.innerHTML.trim().length) {
          this.replaceChildren(...recommendations.children);
        }

        if (recommendations.hasAttribute("data-section-has-card-slider")) {
          this.setAttribute("data-section-has-card-slider", "true");
          initSectionSliderLayout(this);
        } else if (recommendations.hasAttribute("data-is-complementary")) {
          this.initComplementarySlider();
        }

        if (recommendations.hasAttribute("data-products-count")) {
          this.setAttribute(
            "data-products-count",
            recommendations.getAttribute("data-products-count")
          );
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.disableLoading();
        this._initialized = true;
      });
  }

  // The complementary cards sit in the narrow product info column, so the row
  // stays at two cards on every viewport instead of following the shared
  // viewport breakpoints of initSectionSliderLayout. Below $sm the numbers match
  // that shared slider, so the cards line up with the recommendations section.
  initComplementarySlider() {
    const syncComplementarySlideFocusability = (swiper) => {
      swiper.slides.forEach((slide) => {
        slide.toggleAttribute(
          "inert",
          !slide.classList.contains("swiper-slide-visible") &&
            !slide.contains(document.activeElement)
        );
      });
    };

    const sliderEl = this.querySelector(".js-complementary-slider");
    if (!sliderEl) return;

    this.destroyComplementarySlider();

    const prevBtn = this.querySelector(".slider-nav-button--prev");
    const nextBtn = this.querySelector(".slider-nav-button--next");
    const paginationEl = this.querySelector(".slider-pagination");
    const hasNavigation = sliderEl.getAttribute("data-navigation") === "true";
    const hasPagination = sliderEl.getAttribute("data-pagination") === "true";
    const hasAutoplay = sliderEl.getAttribute("data-autoplay") === "true";
    const pauseAutoplayOnHover =
      sliderEl.getAttribute("data-autoplay-pause-on-hover") === "true";
    const autoplayDelay = Number(sliderEl.dataset.autoplayDelay || 8000);

    // the row is two-up at every breakpoint, so a loop needs four slides to keep
    // the track filled — under that Swiper drops loop mode anyway
    const slidesTotal = sliderEl.querySelectorAll(".swiper-slide").length;
    const sliderLoop =
      sliderEl.getAttribute("data-loop") === "true" && slidesTotal >= 4;

    const sliderSettings = {
      loop: sliderLoop,
      slidesPerView: 2,
      spaceBetween: 12,
      speed: 800,
      watchSlidesProgress: true,
      allowTouchMove: true,
      mousewheel: {
        forceToAxis: true,
      },
      a11y: {
        // loop mode reorders slide nodes on focus, which corrupts the tab order
        scrollOnFocus: !sliderLoop,
      },
      breakpoints: {
        576: {
          spaceBetween: 16,
        },
      },
      autoplay: hasAutoplay
        ? {
            delay: autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: pauseAutoplayOnHover,
          }
        : false,
      navigation:
        hasNavigation && prevBtn && nextBtn
          ? {
              nextEl: nextBtn,
              prevEl: prevBtn,
              disabledClass: "swiper-button-disabled",
            }
          : false,
      pagination:
        hasPagination && paginationEl
          ? {
              el: paginationEl,
              type: "progressbar",
            }
          : false,
      // Same contract as the shared card slider: with focus sliding off in loop
      // mode, cards parked off-screen leave the tab order and the arrows bring
      // them in.
      on: sliderLoop
        ? {
            init: syncComplementarySlideFocusability,
            resize: syncComplementarySlideFocusability,
            update: syncComplementarySlideFocusability,
            transitionEnd: syncComplementarySlideFocusability,
          }
        : {},
    };

    this.recommendationsSlider = new Swiper(sliderEl, sliderSettings);

    if (hasAutoplay && this.recommendationsSlider) {
      this.autoplayObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!this.recommendationsSlider?.autoplay) return;

          if (entry.isIntersecting) {
            this.recommendationsSlider.autoplay.resume();
          } else {
            this.recommendationsSlider.autoplay.pause();
          }
        });
      });

      this.autoplayObserver.observe(this);
    }
  }

  destroyComplementarySlider() {
    if (this.autoplayObserver) {
      this.autoplayObserver.disconnect();
      this.autoplayObserver = null;
    }

    if (!this.recommendationsSlider) return;

    this.recommendationsSlider.destroy(true, true);
    this.recommendationsSlider = null;
  }

  enableLoading() {
    const loadingEl = this.querySelector(".product-recommendations-loading");
    if (loadingEl) loadingEl.style.display = "flex";
  }

  disableLoading() {
    const loadingEl = this.querySelector(".product-recommendations-loading");
    if (loadingEl) loadingEl.remove();
  }
}

if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}

class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector(
        'input[name="locale_code"], input[name="country_code"]'
      ),
      button: this.querySelector("button"),
      panel: this.querySelector("ul"),
    };

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.elements.button.addEventListener("click", this.togglePanel.bind(this));
    this.addEventListener("keydown", this.onEscapePress.bind(this));

    this.querySelectorAll("a").forEach((item) =>
      item.addEventListener("click", this.onItemClick.bind(this))
    );
  }

  connectedCallback() {
    document.addEventListener("click", this.handleDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.handleDocumentClick);
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.hidePanel();
    }
  }

  hidePanel() {
    this.elements.button.setAttribute("aria-expanded", "false");
    this.elements.panel.setAttribute("hidden", true);
  }

  showPanel() {
    this.elements.button.setAttribute("aria-expanded", "true");
    this.elements.panel.removeAttribute("hidden");
  }

  togglePanel() {
    if (this.elements.button.getAttribute("aria-expanded") === "true") {
      this.hidePanel();
    } else {
      this.showPanel();
    }
  }

  onEscapePress(event) {
    if (event.key === "Escape") {
      this.hidePanel();
    }
  }

  onItemClick(event) {
    event.preventDefault();
    this.elements.input.value = event.currentTarget.dataset.value;
    this.querySelector("form")?.submit();
    this.hidePanel();
  }
}

if (!customElements.get("localization-form")) {
  customElements.define("localization-form", LocalizationForm);
}

class CountdownTimer extends HTMLElement {
  constructor() {
    super();

    this.enableMockTimer =
      this.getAttribute("data-enable-mock-timer") === "true";

    if (this.enableMockTimer) {
      // get data for infinite mock timer
      const INFINITE_DAYS = Number(this.getAttribute("data-mock-end-day") || 2);
      const DIFF_HOURS = Number(this.getAttribute("data-mock-end-hour") || 3);
      const now = new Date();
      const futureDate = new Date(
        now.getTime() + (INFINITE_DAYS * 24 + DIFF_HOURS) * 60 * 60 * 1000
      );
      const year = futureDate.getFullYear();
      const month = String(futureDate.getMonth() + 1).padStart(2, "0");
      const day = String(futureDate.getDate()).padStart(2, "0");
      const hours = String(futureDate.getHours()).padStart(2, "0");
      const minutes = String(futureDate.getMinutes()).padStart(2, "0");
      this.endDate = `${year}-${month}-${day}`;
      this.endTime = `${hours}:${minutes}`;
    } else {
      // get data from section settings
      this.endDate = this.getAttribute("data-end-date");
      this.endTime = this.getAttribute("data-end-time");
    }
    // ----------------------------------------------------------------
    this.enableAnimation =
      this.getAttribute("data-enable-animation") === "true";
    this.reducedMotionQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );
    this.interval;
    this.isStarted = false;
  }

  connectedCallback() {
    this.setInterval(this.endDate, this.endTime);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  onInit(endDate, endTime) {
    this.completeBehavior = this.getAttribute("data-complete-behavior");
    this.parentEl = this.closest(".block-countdown-timer");
    this.countdownTimerEl = this.querySelector(".countdown-timer__timer");
    this.countdownEndEl = this.querySelector(".countdown-timer__end");
    this.daysEl = this.querySelector(".js-countdown-timer-days");
    this.hoursEl = this.querySelector(".js-countdown-timer-hours");
    this.minutesEl = this.querySelector(".js-countdown-timer-minutes");
    this.secondsEl = this.querySelector(".js-countdown-timer-seconds");
    // ----------------------------------------------------------------
    const countdownDate = new Date(`${endDate}T${endTime}`);
    const now = new Date();
    const distance = countdownDate.getTime() - now.getTime();
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);
    // ----------------------------------------------------------------
    if (distance < 0 && this.completeBehavior === "hide_block") {
      if (this.parentEl) {
        this.parentEl.style.display = "none";
      }
      this.style.display = "none";
      clearInterval(this.interval);
    } else if (distance < 0 && this.completeBehavior === "show_message") {
      if (this.countdownTimerEl) {
        this.countdownTimerEl.style.display = "none";
      }
      if (this.countdownEndEl) {
        this.countdownEndEl.style.display = "flex";
      }
      clearInterval(this.interval);
    } else {
      // no roll on the first pass — only on later value changes
      const animate =
        this.enableAnimation &&
        this.isStarted &&
        !this.reducedMotionQuery.matches;

      this.updateUnit(this.daysEl, this.getTwoDigitTime(days), animate);
      this.updateUnit(this.hoursEl, this.getTwoDigitTime(hours), animate);
      this.updateUnit(this.minutesEl, this.getTwoDigitTime(minutes), animate);
      this.updateUnit(this.secondsEl, this.getTwoDigitTime(seconds), animate);
      this.isStarted = true;
    }
  }

  updateUnit(unitEl, value, animate) {
    if (!unitEl) return;

    const chars = String(value).split("");
    const digits = unitEl.querySelectorAll(".countdown-timer__digit");

    // digit count changed (e.g. 100 -> 99 days): rebuild the clip boxes, no roll
    if (digits.length !== chars.length) {
      unitEl.textContent = "";
      chars.forEach((char) => {
        const box = document.createElement("span");
        box.className = "countdown-timer__digit";
        const digitValue = document.createElement("span");
        digitValue.className = "countdown-timer__digit-value";
        digitValue.textContent = char;
        box.appendChild(digitValue);
        unitEl.appendChild(box);
      });
      return;
    }

    digits.forEach((box, index) => {
      this.updateDigit(box, chars[index], animate);
    });
  }

  updateDigit(box, char, animate) {
    const values = box.querySelectorAll(".countdown-timer__digit-value");
    const current = values[values.length - 1];
    if (!current || current.textContent === char) return;

    // drop any digit still rolling out so fast ticks never stack
    for (let i = 0; i < values.length - 1; i += 1) {
      values[i].remove();
    }

    if (!animate) {
      current.classList.remove("countdown-timer__digit-value--out");
      current.textContent = char;
      return;
    }

    const next = document.createElement("span");
    next.className =
      "countdown-timer__digit-value countdown-timer__digit-value--in";
    next.textContent = char;
    box.appendChild(next);

    current.classList.add("countdown-timer__digit-value--out");
    current.addEventListener("transitionend", () => current.remove(), {
      once: true,
    });

    // reflow so the incoming digit starts below, then release it to roll up
    void next.offsetWidth;
    next.classList.remove("countdown-timer__digit-value--in");
  }

  getTwoDigitTime(time) {
    if (String(time).length === 1) {
      return "0" + time;
    }
    if (String(time) === "-1") {
      return "00";
    }
    return time;
  }

  setInterval(endDate, endTime) {
    clearInterval(this.interval);
    this.interval = setInterval(this.onInit.bind(this, endDate, endTime), 1000);
  }
}

if (!customElements.get("countdown-timer")) {
  customElements.define("countdown-timer", CountdownTimer);
}

// Region/language modal: the trigger (modal-region-selector) lives in a section,
// the modal (region-modal) renders once at the end of the body. Item clicks only
// store the choice; a single Save submit applies country and language together.
// The modal is self-contained: its own .region-modal__overlay dim element (below
// the content, above the header) closes it on click. A trigger click also closes
// an open header navigation panel (one-way handoff, see ModalRegionSelector).
class RegionModal extends HTMLElement {
  constructor() {
    super();

    this._onClose = this.close.bind(this);
    this._onKeyDown = this.onKeyDown.bind(this);
    this._onTabsClick = this.onTabsClick.bind(this);
    this._onSearchInput = this.onSearchInput.bind(this);
    this._onSearchReset = this.onSearchReset.bind(this);
    this._onSubmit = this.onSubmit.bind(this);
    this._onBodyScroll = this.onBodyScroll.bind(this);
  }

  connectedCallback() {
    this.contentEl = this.querySelector(".region-modal__content");
    this.bodyEl = this.querySelector(".region-modal__body");
    this.overlayEl = this.querySelector(".region-modal__overlay");
    this.closeBtn = this.querySelector(".js-region-close-trigger");
    this.tabsEl = this.querySelector(".region-modal__tabs");
    this.tabs = this.querySelectorAll(".region-modal__tab");
    this.panes = this.querySelectorAll(".region-modal__pane");
    this.searchInput = this.querySelector(".js-region-search-input");
    this.searchResetBtn = this.querySelector(".js-region-search-reset");
    this.noResultsEl = this.querySelector("[data-no-results]");
    this.searchItems = this.querySelectorAll("[data-searchable]");
    this.formEl = this.querySelector(".region-modal__form");

    this.overlayEl?.addEventListener("click", this._onClose);
    this.closeBtn?.addEventListener("click", this._onClose);
    this.tabsEl?.addEventListener("click", this._onTabsClick);
    this.searchInput?.addEventListener("input", this._onSearchInput);
    this.searchResetBtn?.addEventListener("click", this._onSearchReset);
    this.formEl?.addEventListener("submit", this._onSubmit);
    this.bodyEl?.addEventListener("scroll", this._onBodyScroll, {
      passive: true,
    });
    this.onBodyScroll();
    document.addEventListener("keydown", this._onKeyDown);
  }

  disconnectedCallback() {
    this.bodyEl?.removeEventListener("scroll", this._onBodyScroll);
    document.removeEventListener("keydown", this._onKeyDown);
    if (this.getAttribute("open")) {
      document.body.classList.remove("overflow-hidden-drawer");
    }
  }

  open(trigger, focusReturnEl) {
    if (trigger) {
      ["showRegion", "showLanguage", "showFlag", "showSearch"].forEach(
        (key) => {
          if (trigger.dataset[key]) this.dataset[key] = trigger.dataset[key];
        }
      );
    }

    this.activeElement = focusReturnEl || null;
    this.activeElement?.setAttribute("aria-expanded", "true");

    const regionVisible = this.dataset.showRegion !== "false";
    const requestedTab = focusReturnEl?.dataset.tabId;
    const requestedPane =
      requestedTab &&
      this.querySelector(`.region-modal__pane[data-tab-id="${requestedTab}"]`);
    this.switchTab(
      requestedPane ? requestedTab : regionVisible ? "region" : "language"
    );

    this.setAttribute("open", "true");
    document.body.classList.add("overflow-hidden-drawer");

    // transitionend can fail to fire; guard so focus enters the dialog once
    let focusEntered = false;
    const enterFocus = () => {
      if (focusEntered) return;
      focusEntered = true;
      if (this.getAttribute("open")) trapFocus(this, this.contentEl);
    };
    this.addEventListener("transitionend", enterFocus, { once: true });
    setTimeout(enterFocus, 350);
  }

  close() {
    if (!this.getAttribute("open")) return;

    this.removeAttribute("open");
    document.body.classList.remove("overflow-hidden-drawer");

    if (this.activeElement) {
      this.activeElement.setAttribute("aria-expanded", "false");
      // opened from the mobile drawer: hand focus back to the drawer's trap
      const drawer = this.activeElement.closest(".menu-drawer-container[open]");
      drawer
        ? trapFocus(drawer, this.activeElement)
        : removeTrapFocus(this.activeElement);
      this.activeElement = null;
    } else {
      removeTrapFocus();
    }
  }

  onKeyDown(event) {
    if (event.key === "Escape" && this.getAttribute("open")) this.close();
  }

  switchTab(tabId) {
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.tabId === tabId;
      tab.classList.toggle("active", isActive);
      tab.setAttribute("aria-selected", String(isActive));
    });
    this.panes.forEach((pane) => {
      pane.classList.toggle("active", pane.dataset.tabId === tabId);
    });
    this.setAttribute("data-active-tab", tabId);
    this.onBodyScroll();
  }

  // top scroll shadow: shown while the list is scrolled under the head
  onBodyScroll() {
    if (!this.bodyEl) return;
    this.classList.toggle("region-modal--scrolled", this.bodyEl.scrollTop > 0);
  }

  onTabsClick(event) {
    const tab = event.target.closest(".region-modal__tab");
    if (!tab) return;
    this.switchTab(tab.dataset.tabId);
  }

  onSearchInput() {
    const query = this.searchInput.value.trim().toLowerCase();
    let matches = 0;

    this.searchItems.forEach((item) => {
      const isMatch = !query || item.dataset.searchable.includes(query);
      item.toggleAttribute("hidden", !isMatch);
      if (isMatch) matches += 1;
    });

    this.noResultsEl?.toggleAttribute("hidden", matches > 0);
    this.searchResetBtn?.toggleAttribute("hidden", !query);
  }

  onSearchReset() {
    if (!this.searchInput) return;
    this.searchInput.value = "";
    this.onSearchInput();
    this.searchInput.focus();
  }

  // one submit applies both pending changes through the hidden form inputs
  onSubmit() {
    const countryInput = this.formEl.querySelector(
      'input[name="country_code"]'
    );
    const localeInput = this.formEl.querySelector('input[name="locale_code"]');
    const checkedCountry = this.querySelector(
      'input[name="RegionModalCountry"]:checked'
    );
    const checkedLanguage = this.querySelector(
      'input[name="RegionModalLanguage"]:checked'
    );

    if (countryInput && checkedCountry)
      countryInput.value = checkedCountry.value;
    if (localeInput && checkedLanguage)
      localeInput.value = checkedLanguage.value;
  }
}

if (!customElements.get("region-modal")) {
  customElements.define("region-modal", RegionModal);
}

class ModalRegionSelector extends HTMLElement {
  constructor() {
    super();
    this._onTriggerClick = this.onTriggerClick.bind(this);
  }

  connectedCallback() {
    this.triggers = this.querySelectorAll(".js-region-modal-trigger");
    this.triggers.forEach((trigger) =>
      trigger.addEventListener("click", this._onTriggerClick)
    );
  }

  disconnectedCallback() {
    this.triggers?.forEach((trigger) =>
      trigger.removeEventListener("click", this._onTriggerClick)
    );
  }

  onTriggerClick(event) {
    const modal = document.getElementById("RegionModal");
    if (!modal || typeof modal.open !== "function") return;

    // handoff: an open desktop navigation panel slides away while the modal
    // opens in parallel (both run their own transitions — no overlay flicker)
    window.HeaderMegaModals?.closePanel?.();

    modal.open(this, event.currentTarget);
  }
}

if (!customElements.get("modal-region-selector")) {
  customElements.define("modal-region-selector", ModalRegionSelector);
}
